/**
 * 内置离线「常用字 → 拼音」数据表（**自动生成，请勿手工编辑**）。
 *
 * 生成方式：scripts/make-pinyin-data.py（在 Linux 上跑，避免 Windows 侧
 * 中文编码损坏）。重新生成只需把上游文件重新下载后重跑该脚本。
 *
 * ---------------------------------------------------------------- 来源与许可
 *
 * 数据取自 [mozillazg/pinyin-data](https://github.com/mozillazg/pinyin-data)：
 *
 *   · kMandarin_8105.txt —— 《通用规范汉字表》(2013 年版) 8105 字的
 *     **最常用读音**（主读音；含作者手工纠正 kMandarin_overwrite.txt）。
 *   · kMandarin.txt —— Unihan kMandarin 全部读音，用作多音字的**备选列表**。
 *
 * pinyin-data 采用 **MIT License**（Copyright (c) 2016 mozillazg），完整许可
 * 文本随仓库保留在 scripts/THIRD-PARTY-pinyin-data-LICENSE.txt。
 * 其底层 Unihan 数据版权见 <https://www.unicode.org/license.txt>（Unicode License v3）。
 *
 * 选它的理由：许可宽松（MIT）、**离线**、带声调符号，且上游已按
 * 《通用规范汉字表》裁剪过。不引入任何 npm 依赖，也不需要联网。
 *
 * ---------------------------------------------------------------- 多音字怎么办
 *
 * **每个字只给一个「最常用读音」作为默认值**（PY_TABLE 的第一项），
 * 另附 PY_ALT 备选读音串，供家长在界面上人工覆盖时挑选（见 resolvePinyin）。
 * 刻意**不做**多音字消歧：本项目面向听写，读错音会教坏孩子，所以宁可
 * 「默认值可能不对 + 一键改成对的那个」，也不要「看起来很聪明但偶尔错得莫名其妙」。
 * 词级同理：joinWordPinyin 按字拼，整词拼音永远可以人工覆盖。
 *
 * 格式：字符:主读音[,备选读音...]，逗号分隔，每行 8 条。
 *
 * ⚠️ 只保留 BMP 内的字（<= U+FFFF）：上游《通用规范汉字表》里有少量
 *   CJK 扩展 B 区罕用字，小学课本永远不会出现，直接裁掉。
 */

/** 原始数据：换行分隔条目、逗号分隔条目内的字段。 */
export const PINYIN_TABLE_SOURCE = `
㑇:zhòu,㑊:yì,㕮:fǔ,㘎:hǎn,㙍:duō,㙘:yāo,㙦:xié,㛃:jié,
㛚:tǒng,yǒng,㛹:pián,㟃:sī,㠇:jiù,㠓:méng,㢜:kāng,kàng,㤘:zhòu,chù,㥄:líng,
㧐:sǒng,㧑:huī,wéi,㧟:kuǎi,㫰:làng,lǎng,㬊:huàn,huǎn,㬎:xiǎn,㬚:chè,㭎:gāng,
㭕:qū,㮾:lǎng,㰀:lí,㳇:fù,㳘:chōng,㳚:xù,yù,㴔:jí,sè,yì,xì,㵐:jué,
㶲:yòng,㸆:kào,㸌:huò,㺄:yǔ,yú,㻬:tū,tú,㽏:gàn,㿠:huàng,䁖:lōu,
䂮:lüè,䃅:dī,䃎:zhà,zhǎ,䅟:cǎn,䅿:huá,huáng,䌹:jiǒng,䎃:rǎn,䎖:zēng,
䏝:zhuān,chún,䏡:shì,䏲:dié,tī,䐃:jùn,jiǒng,zhūn,䓖:qióng,䓛:qū,fǔ,䓨:yīng,䓫:qí,jì,
䓬:zhuó,䗖:dì,䗛:xiū,䗪:zhè,䗴:tíng,䚾:rén,nín,䜣:xīn,xī,䝙:chū,
䢺:chū,䢼:gōng,䣘:táng,䥽:pō,䦃:zhuō,䭸:fù,䭻:fēn,fù,䲟:yìn,
䲠:chūn,䲢:téng,䳉:dōng,dàn,䴓:shī,䴔:jiāo,䴕:liè,䴖:jīng,䴗:jú,
䴘:tī,䴙:pì,䵻:huì,gǔ,䶮:yǎn,一:yī,yí,yì,丁:dīng,zhēng,七:qī,qí,万:wàn,mò,
丈:zhàng,三:sān,上:shàng,shǎng,下:xià,不:bù,fǒu,fōu,fū,bú,与:yǔ,yù,yú,丏:miǎn,丐:gài,
丑:chǒu,专:zhuān,且:qiě,jū,cú,丕:pī,世:shì,丘:qiū,丙:bǐng,bìng,业:yè,
丛:cóng,东:dōng,丝:sī,丞:chéng,shèng,zhēng,zhěng,丢:diū,两:liǎng,严:yán,丧:sàng,sāng,
个:gè,gě,gàn,丫:yā,中:zhōng,zhòng,丰:fēng,串:chuàn,guàn,quàn,临:lín,丸:wán,丹:dān,
为:wèi,wéi,主:zhǔ,zhù,丽:lì,lí,举:jǔ,乂:yì,ài,乃:nǎi,ǎi,久:jiǔ,么:me,yāo,mó,ma,
义:yì,之:zhī,zhū,zhì,乌:wū,wù,乍:zhà,zuò,乎:hū,乏:fá,乐:lè,yuè,乒:pīng,
乓:pāng,乔:qiáo,乖:guāi,乘:chéng,shèng,乙:yǐ,yì,jué,乜:miē,niè,九:jiǔ,jiū,乞:qǐ,qì,
也:yě,yí,习:xí,乡:xiāng,书:shū,乩:jī,买:mǎi,乱:luàn,乳:rǔ,
乸:nǎ,乾:qián,gān,了:le,liǎo,liào,予:yǔ,yú,zhù,争:zhēng,事:shì,zì,二:èr,亍:chù,
于:yú,wéi,yū,xū,亏:kuī,yú,云:yún,互:hù,亓:qí,五:wǔ,井:jǐng,jìng,亘:gèn,xuān,gèng,
亚:yà,些:xiē,suò,suō,亟:jí,qì,亡:wáng,wú,亢:kàng,gāng,gēng,交:jiāo,亥:hài,jiē,亦:yì,
产:chǎn,亨:hēng,xiǎng,pēng,亩:mǔ,享:xiǎng,京:jīng,亭:tíng,亮:liàng,liáng,亲:qīn,qìng,
亳:bó,亵:xiè,亶:dǎn,dàn,chán,zhān,亸:duǒ,亹:wěi,mén,人:rén,亿:yì,什:shén,shí,
仁:rén,仂:lè,lì,仃:dīng,dǐng,仄:zè,仅:jǐn,fù,nú,jìn,仆:pū,pú,仇:chóu,qiú,jū,仉:zhǎng,
今:jīn,介:jiè,gè,仍:réng,从:cóng,zòng,仑:lún,仓:cāng,仔:zǎi,zǐ,zī,仕:shì,
他:tā,tuó,仗:zhàng,付:fù,仙:xiān,xiǎn,仝:tóng,仞:rèn,仟:qiān,仡:gē,yì,wù,
代:dài,令:lìng,líng,lǐng,lián,以:yǐ,sì,仨:sā,仪:yí,仫:mù,们:men,mén,仰:yǎng,áng,
仲:zhòng,仳:pǐ,pí,bì,仵:wǔ,件:jiàn,móu,价:jià,jie,jiè,任:rèn,rén,lìn,份:fèn,bīn,仿:fǎng,páng,
企:qǐ,伈:xǐn,lǐn,伉:kàng,gāng,kǎng,伊:yī,伋:jí,fán,伍:wǔ,伎:jì,zhì,qí,qì,伏:fú,fù,
伐:fá,休:xiū,xù,众:zhòng,yín,优:yōu,yóu,伙:huǒ,huo,会:huì,kuài,伛:yǔ,伞:sǎn,
伟:wěi,传:chuán,zhuàn,伢:yá,伣:qiàn,伤:shāng,伥:chāng,伦:lún,伧:cāng,chen,
伪:wěi,伫:zhù,伭:xián,伯:bó,bǎi,mò,bà,估:gū,gù,伲:nì,nǐ,ní,伴:bàn,pàn,伶:líng,
伸:shēn,伺:cì,sì,似:shì,sì,伽:gā,jiā,qié,伾:pī,佁:yǐ,ǎi,sì,chì,佃:diàn,tián,但:dàn,tǎn,yàn,
位:wèi,lì,低:dī,住:zhù,佐:zuǒ,佑:yòu,体:tǐ,tī,bèn,cuì,何:hé,hè,佖:bì,
佗:tuó,tuō,tuò,yí,佘:shé,余:yú,tú,xú,yù,佚:yì,dié,佛:fú,fó,bó,bì,作:zuò,zuō,zuó,佝:gōu,kòu,jū,佞:nìng,
佟:tóng,你:nǐ,佣:yōng,yòng,佤:wǎ,佥:qiān,佩:pèi,佬:lǎo,liáo,佯:yáng,
佰:bǎi,mò,佳:jiā,佴:èr,nài,佶:jí,佸:huó,佺:quán,佻:tiāo,tiáo,tiào,diǎo,yáo,dào,zhào,佼:jiǎo,jiāo,xiáo,
佽:cì,佾:yì,使:shǐ,侁:shēn,侂:tuō,侃:kǎn,侄:zhí,侈:chǐ,
侉:kuǎ,huá,è,wú,例:lì,liè,侍:shì,侏:zhū,zhōu,侑:yòu,侔:móu,máo,侗:dòng,tōng,tóng,tǒng,侘:chà,
供:gōng,gòng,依:yī,yǐ,侠:xiá,侣:lǚ,侥:jiǎo,yáo,侦:zhēn,侧:cè,zè,zhāi,侨:qiáo,
侩:kuài,侪:chái,侬:nóng,侮:wǔ,侯:hóu,hòu,侴:chǒu,hào,侵:qīn,qǐn,侹:tǐng,
便:biàn,pián,biān,促:cù,chuò,俄:é,俅:qiú,俊:jùn,shùn,dūn,俍:liáng,lǎng,俎:zǔ,俏:qiào,xiào,xiāo,
俐:lì,俑:yǒng,俗:sú,俘:fú,俙:xī,俚:lǐ,lì,俜:pīng,保:bǎo,
俞:yú,shù,俟:qí,sì,信:xìn,shēn,俣:yǔ,俦:chóu,俨:yǎn,俩:liǎ,liǎng,俪:lì,
俫:lái,俭:jiǎn,修:xiū,俯:fǔ,俱:jù,jū,俳:pái,俵:biào,俶:chù,shū,tì,
俸:fèng,běng,俺:ǎn,yàn,俾:bǐ,bì,bēi,pì,倌:guān,倍:bèi,péi,倏:shū,倒:dào,dǎo,倓:tán,dàn,tàn,
倔:jué,juè,倕:chuí,zhuì,倘:tǎng,cháng,候:hòu,倚:yǐ,jī,yī,倜:tì,diào,zhōu,倞:jìng,liàng,借:jiè,
倡:chàng,chāng,倥:kōng,kǒng,倦:juàn,倧:zōng,倨:jù,倩:qiàn,qìng,倪:ní,nì,niè,倬:zhuō,
倭:wō,wēi,wǒ,倮:luǒ,倴:bèn,bēn,债:zhài,倻:yē,值:zhí,倾:qīng,偁:chēng,
偃:yǎn,假:jiǎ,jià,jie,xià,xiá,gé,偈:jì,jié,qì,偌:ruò,rè,偎:wēi,偏:piān,偓:wò,偕:xié,jiē,
做:zuò,停:tíng,偡:zhàn,健:jiàn,偬:zǒng,cōng,偭:miǎn,偰:xiè,偲:cāi,sī,sǐ,
偶:ǒu,偷:tōu,偻:lóu,lǚ,偾:fèn,偿:cháng,傀:guī,kuǐ,kuài,傃:sù,傅:fù,fū,
傈:lì,傉:nù,傍:bàng,páng,bēng,péng,傒:xī,xì,傕:jué,què,傣:dǎi,傥:tǎng,傧:bīn,
储:chǔ,傩:nuó,催:cuī,傲:ào,áo,傺:chì,傻:shǎ,僇:lù,liáo,僎:zhuàn,zūn,
像:xiàng,僔:zǔn,cuán,僖:xī,僚:liáo,liǎo,lǎo,僦:jiù,僧:sēng,céng,僬:jiāo,jiào,jiǎo,僭:jiàn,zèn,
僮:tóng,zhuàng,chòng,僰:bó,僳:sù,僵:jiāng,僻:pì,儆:jǐng,儇:xuān,xuán,儋:dān,dàn,shàn,
儒:rú,儡:lěi,léi,lèi,儦:biāo,儳:chán,chàn,儴:ráng,xiāng,儿:ér,er,rén,兀:wù,wū,允:yǔn,yuán,
元:yuán,兄:xiōng,kuàng,充:chōng,兆:zhào,先:xiān,光:guāng,guàng,克:kè,免:miǎn,wèn,wǎn,
兑:duì,ruì,duó,兔:tù,tú,chān,兕:sì,兖:yǎn,党:dǎng,兜:dōu,兢:jīng,入:rù,
全:quán,八:bā,bá,公:gōng,六:liù,lù,兮:xī,兰:lán,共:gòng,gōng,gǒng,hóng,关:guān,
兴:xīng,xìng,兵:bīng,其:qí,jī,jì,具:jù,典:diǎn,tiǎn,兹:zī,cí,养:yǎng,兼:jiān,
兽:shòu,冀:jì,冁:chǎn,内:nèi,nà,ruì,冈:gāng,冉:rǎn,nán,dān,册:cè,zhà,再:zài,
冏:jiǒng,jiōng,冒:mào,mò,冔:xǔ,xú,冕:miǎn,冗:rǒng,写:xiě,xiè,军:jūn,农:nóng,
冠:guān,guàn,冢:zhǒng,冤:yuān,冥:míng,mián,miàn,冬:dōng,冮:gāng,冯:féng,píng,冰:bīng,níng,
冱:hù,冲:chōng,chòng,决:jué,况:kuàng,冶:yě,冷:lěng,líng,lǐng,冻:dòng,冼:xiǎn,shěng,
冽:liè,净:jìng,chēng,凄:qī,准:zhǔn,凇:sōng,凉:liáng,liàng,凋:diāo,凌:líng,lìng,
减:jiǎn,凑:còu,凓:lì,凘:sī,凛:lǐn,凝:níng,几:jǐ,jī,凡:fán,
凤:fèng,凫:fú,凭:píng,凯:kǎi,凰:huáng,凳:dèng,凶:xiōng,凸:tū,
凹:āo,wā,出:chū,击:jī,凼:dàng,函:hán,凿:záo,zuò,刀:dāo,diāo,刁:diāo,
刃:rèn,分:fēn,fèn,fén,切:qiè,qiē,qì,刈:yì,刊:kān,刍:chú,刎:wěn,刑:xíng,
划:huà,huá,guò,guǒ,huai,刖:yuè,列:liè,lì,刘:liú,则:zé,刚:gāng,创:chuàng,chuāng,初:chū,
删:shān,判:pàn,刨:páo,bào,利:lì,别:bié,biè,刬:chǎn,chàn,刭:jǐng,刮:guā,
到:dào,刳:kū,kōu,制:zhì,刷:shuā,shuà,券:quàn,xuàn,刹:shā,chà,刺:cì,cī,qì,刻:kè,kēi,
刽:guì,刿:guì,剀:kǎi,剁:duò,剂:jì,剃:tì,剅:lóu,dōu,削:xuē,xiāo,qiào,shào,
剋:kè,kēi,剌:lá,là,前:qián,jiǎn,剐:guǎ,剑:jiàn,剔:tī,tì,剕:fèi,剖:pōu,pǒ,
剜:wān,剞:jī,剟:duō,chì,剡:shàn,yǎn,剥:bō,bāo,pū,剧:jù,剩:shèng,剪:jiǎn,
副:fù,pì,割:gē,剽:piāo,piào,piáo,biǎo,biāo,剿:jiǎo,chāo,劁:qiāo,qiáo,劂:jué,劄:zhā,zhá,劈:pī,pǐ,
劐:huō,huò,huá,劓:yì,力:lì,劝:quàn,办:bàn,功:gōng,加:jiā,务:wù,
劢:mài,劣:liè,动:dòng,助:zhù,chú,努:nǔ,劫:jié,劬:qú,劭:shào,
励:lì,劲:jìn,jìng,劳:láo,劼:jié,劾:hé,kài,势:shì,勃:bó,勇:yǒng,
勉:miǎn,勋:xūn,勍:qíng,勐:měng,勒:lēi,lè,lei,勔:miǎn,勖:xù,mào,勘:kān,
勚:yì,募:mù,bó,勠:lù,勤:qín,qí,勰:xié,勺:sháo,shuò,zhuó,dì,勾:gōu,gòu,勿:wù,mò,
匀:yún,jūn,yùn,包:bāo,páo,fú,匆:cōng,匈:xiōng,匍:pú,匏:páo,匐:fú,匕:bǐ,pìn,
化:huà,huā,huò,北:běi,bèi,匙:shi,chí,匜:yí,匝:zā,匠:jiàng,匡:kuāng,wāng,匣:xiá,
匦:guǐ,匪:fěi,fēi,fēn,匮:kuì,guì,匹:pǐ,区:qū,ōu,医:yī,yì,匼:kē,ē,ǎn,匾:biǎn,
匿:nì,tè,十:shí,千:qiān,卅:sà,升:shēng,午:wǔ,卉:huì,半:bàn,pàn,
华:huá,huà,huā,协:xié,卑:bēi,bǐ,bì,pí,bān,卒:zú,cù,cuì,卓:zhuó,zhuō,单:dān,chán,shàn,卖:mài,南:nán,nā,
博:bó,卜:bo,bǔ,pū,卞:biàn,pán,卟:bǔ,jī,占:zhàn,zhān,tiē,卡:kǎ,qiǎ,卢:lú,卣:yǒu,
卤:lǔ,xī,卦:guà,卧:wò,卫:wèi,卬:áng,yǎng,卮:zhī,卯:mǎo,印:yìn,yì,
危:wēi,即:jí,却:què,卵:luǎn,kūn,卷:juǎn,juàn,quán,quān,gǔn,jùn,卸:xiè,卺:jǐn,卿:qīng,
厂:chǎng,hǎn,yán,ān,厄:è,ě,厅:tīng,历:lì,厉:lì,压:yā,yà,厌:yàn,厍:shè,
厕:cè,si,厖:páng,máng,厘:lí,chán,厚:hòu,厝:cuò,jí,原:yuán,厢:xiāng,厣:yǎn,
厥:jué,厦:shà,xià,厨:chú,厩:jiù,厮:sī,去:qù,qū,厾:dū,dǔ,县:xiàn,
叁:sān,参:cān,cēn,shēn,叆:ài,叇:dài,又:yòu,叉:chā,chá,chǎ,chà,及:jí,友:yǒu,
双:shuāng,反:fǎn,fàn,发:fā,fà,叔:shū,叕:zhuó,yǐ,lì,jué,取:qǔ,qū,受:shòu,dào,变:biàn,
叙:xù,叚:jiǎ,xiá,叛:pàn,叟:sǒu,sōu,xiāo,叠:dié,口:kǒu,古:gǔ,gù,kū,句:jù,gōu,gòu,qú,
另:lìng,叨:dāo,tāo,dáo,叩:kòu,只:zhǐ,zhī,叫:jiào,召:zhào,shào,叭:bā,pā,ba,叮:dīng,
可:kě,kè,gē,台:tái,tāi,yí,sì,叱:chì,huà,é,史:shǐ,右:yòu,叵:pǒ,叶:yè,xié,号:hào,háo,xiāo,
司:sī,cí,sì,叹:tàn,yǐ,yòu,叻:lè,lì,叼:diāo,叽:jī,jiào,吁:xū,yū,yù,吃:chī,qī,各:gè,gě,
吆:yāo,合:hé,gě,吉:jí,吊:diào,同:tóng,tòng,名:míng,mìng,后:hòu,吏:lì,
吐:tǔ,tù,向:xiàng,吒:zhā,zhà,吓:xià,hè,hà,吕:lǚ,吖:yā,ā,吗:ma,má,mǎ,君:jūn,
吝:lìn,吞:tūn,tiān,吟:yín,yǐn,jìn,吠:fèi,吡:bǐ,pǐ,bì,吣:qìn,否:fǒu,pǐ,吧:ba,bā,pā,
吨:dūn,tún,tǔn,吩:fēn,pèn,含:hán,hàn,听:tīng,yǐn,yí,吭:kēng,háng,hàng,吮:shǔn,启:qǐ,吱:zhī,zī,qì,
吲:yǐn,shěn,吴:wú,tūn,吵:chǎo,chāo,miǎo,chào,吸:xī,吹:chuī,chuì,吻:wěn,吼:hǒu,吽:hōng,hǒu,ōu,
吾:wú,yú,yá,呀:ya,yā,xiā,呃:è,e,ài,呆:dāi,bǎo,ái,呇:qǐ,mèn,呈:chéng,kuáng,chěng,告:gào,jū,gù,呋:fū,
呐:nà,nè,na,nuò,ne,呒:wǔ,fǔ,ḿ,呓:yì,呔:dāi,tǎi,呕:ǒu,ōu,òu,呖:lì,呗:bei,bài,员:yuán,yùn,yún,
呙:guō,wāi,呛:qiāng,qiàng,呜:wū,呢:ne,ní,nǐ,nī,呣:ḿ,móu,呤:lìng,líng,呦:yōu,周:zhōu,
呱:gū,guā,guǎ,呲:cī,zī,cí,味:wèi,mèi,呵:hē,hā,ā,a,kē,huō,á,ǎ,à,呶:náo,ná,nǔ,呷:gā,xiā,xiá,jiǎ,呸:pēi,呻:shēn,
呼:hū,xiāo,xū,hè,xià,命:mìng,咀:jǔ,zuǐ,咁:gàn,hán,xián,咂:zā,咄:duō,咆:páo,咇:bié,bì,
咉:yāng,yǎng,咋:zǎ,zé,zhā,zhà,和:hé,hè,hú,huó,huò,huo,咍:hāi,tāi,咎:jiù,gāo,咏:yǒng,咐:fù,fú,咒:zhòu,
咔:kā,kǎ,nòng,咕:gū,gu,咖:kā,gā,jiā,咙:lóng,咚:dōng,咛:níng,咝:sī,咡:èr,ér,
咣:guāng,gōng,咤:zhà,咥:xì,dié,xī,zhì,咦:yí,xī,咧:liě,liē,liè,lié,lie,咨:zī,咩:miē,mie,咪:mī,mǐ,miē,mǎi,
咫:zhǐ,咬:yǎo,jiāo,yāo,jiǎo,咯:gē,lo,kǎ,luò,kā,咱:zán,zá,zǎ,zan,咳:ké,hāi,hái,gāi,咴:huī,hái,咸:xián,jiǎn,jiān,咺:xuǎn,xuān,
咻:xiū,xǔ,xiāo,xù,咽:yàn,yān,yè,yuān,咿:yī,哀:āi,品:pǐn,哂:shěn,哃:tóng,哄:hǒng,hōng,hòng,
哆:duō,chǐ,zhà,chì,duò,diě,哇:wa,wā,guī,huá,wá,哈:hā,hǎ,hà,hē,hé,tà,shà,哉:zāi,哌:pài,gū,响:xiǎng,哎:āi,哏:gén,hěn,ǹ,
哐:kuāng,qiāng,哑:yǎ,yā,哒:dá,dā,哓:xiāo,哔:bì,哕:huì,yuě,哗:huā,huá,哙:kuài,
哚:duǒ,哝:nóng,哞:mōu,哟:yō,yo,哢:lòng,哥:gē,哦:ó,é,ò,哧:chī,xià,hè,
哨:shào,sāo,xiāo,xiào,sào,哩:lī,li,lì,lǐ,mái,yīng,哪:nǎ,na,né,nuó,nǎi,nà,niè,něi,哭:kū,哮:xiāo,xiào,xuē,哱:bō,pò,bèi,bā,bó,哲:zhé,哳:zhā,
哺:bǔ,bū,fǔ,哼:hēng,hng,哽:gěng,yǐng,yìng,ńg,ń,哿:gě,唁:yàn,唆:suō,shuà,唇:chún,zhēn,zhèn,唉:āi,ài,ǎi,
唏:xī,xiè,唐:táng,唑:zuò,shì,唔:wú,wù,ńg,ḿ,ń,唛:mà,mài,唝:gòng,hǒng,唠:láo,lào,唢:suǒ,
唣:zào,唤:huàn,唧:jī,jié,唪:fěng,běng,唬:hǔ,xiāo,guó,xià,háo,售:shòu,shú,唯:wéi,wěi,唰:shuā,
唱:chàng,唳:lì,唵:ǎn,ng,n,唷:yō,yù,唼:shà,qiè,唾:tuò,唿:hū,啁:zhāo,zhōu,dāo,tiáo,diào,
啃:kěn,啄:zhuó,zhòu,商:shāng,啉:lín,lán,lèn,啊:a,ā,á,ǎ,à,è,啐:cuì,zú,zá,è,chuài,啕:táo,啖:dàn,
啜:chuài,chuò,zhuó,啡:fēi,pèi,pái,pēi,bài,啤:pí,啥:shá,shà,啦:la,lā,啧:zé,啪:pā,啫:zhě,
啬:sè,啭:zhuàn,啮:niè,啰:luō,luó,luo,啴:chǎn,tān,啵:bō,bo,啶:dìng,啷:lāng,
啸:xiào,啻:chì,dì,啼:tí,啾:jiū,喀:kā,kè,ke,喁:yóng,yú,喂:wèi,喃:nán,nǎn,
善:shàn,喆:zhé,喇:lǎ,lá,lā,la,喈:jiē,xiè,喉:hóu,喊:hǎn,kàn,jiān,喋:dié,zhá,qiè,喏:nuò,rě,
喑:yīn,yǐn,yìn,喔:ō,wō,wū,o,ò,喘:chuǎn,喙:huì,zhòu,喜:xǐ,xī,chì,喝:hē,hè,yè,kài,喟:kuì,huài,喤:huáng,
喧:xuān,xuǎn,喱:lí,喳:zhā,chā,zha,喵:miāo,喷:pēn,pèn,喹:kuí,喻:yù,yú,喽:lóu,lou,
喾:kù,嗄:á,shà,a,xià,嗅:xiù,嗉:sù,嗌:ài,yì,wò,嗍:suō,shuò,嗐:hài,嗑:kē,kè,hé,xiá,
嗒:dā,tà,da,嗓:sǎng,嗔:chēn,tián,嗖:sōu,sù,sòu,嗜:shì,嗝:gé,嗞:zī,嗟:jiē,jiè,juē,
嗡:wēng,wěng,嗣:sì,嗤:chī,嗥:háo,嗦:suo,suō,嗨:hāi,hēi,嗪:qín,嗫:niè,
嗬:hē,嗯:ń,ǹg,ńg,ňg,ň,ǹ,嗲:diē,diǎ,嗳:āi,ǎi,ài,嗵:tōng,嗷:áo,嗽:sòu,shuò,shù,嗾:sǒu,
嘀:dí,zhé,dī,嘁:qī,zú,zā,嘈:cáo,嘉:jiā,嘌:piào,piāo,嘎:gā,gá,gǎ,嘏:gǔ,jiǎ,嘘:xū,shī,
嘚:dē,dēi,dé,dāi,嘛:ma,má,嘞:lei,lē,嘟:dū,嘡:tāng,嘣:bēng,嘤:yīng,嘧:mì,
嘬:chuài,zuō,嘭:pēng,嘱:zhǔ,嘲:cháo,zhāo,嘴:zuǐ,嘶:sī,嘹:liáo,liào,嘻:xī,
嘿:hēi,mò,mù,噀:xùn,噂:zǔn,zūn,噇:chuáng,噌:cēng,chēng,噍:jiào,jiāo,jiū,噎:yē,yì,shà,噔:dēng,
噗:pū,噘:juē,噙:qín,噜:lū,噢:ō,yǔ,yù,ào,噤:jìn,器:qì,噩:è,
噪:zào,噫:yī,ǎi,yì,噬:shì,噱:jué,xué,噶:gá,gé,噻:sāi,噼:pī,嚄:huō,huò,wò,ǒ,
嚅:rú,嚆:hāo,嚎:háo,嚏:tì,嚓:cā,chā,嚚:yín,嚣:xiāo,áo,嚭:pǐ,
嚯:huò,xuè,嚷:rǎng,rāng,嚼:jué,jiáo,jiào,囊:náng,nāng,囔:nāng,nang,囚:qiú,四:sì,回:huí,
囟:xìn,因:yīn,囡:nān,niè,团:tuán,qiú,囤:dùn,tún,囫:hú,园:yuán,wán,困:kùn,
囱:cōng,chuāng,围:wéi,囵:lún,囷:qūn,囹:líng,固:gù,国:guó,图:tú,
囿:yòu,圃:pǔ,圄:yǔ,圆:yuán,圈:quān,juān,juàn,quán,juǎn,圉:yǔ,圊:qīng,圌:chuán,chuí,
圐:kū,圙:lüè,圜:huán,yuán,土:tǔ,dù,chǎ,tú,圢:tǐng,圣:shèng,kū,在:zài,圩:wéi,xū,yú,
圪:gē,yì,圫:yù,tuō,zhūn,圬:wū,圭:guī,圮:pǐ,圯:yí,地:dì,de,圲:qiān,
圳:zhèn,quǎn,chóu,huái,圹:kuàng,场:chǎng,cháng,圻:qí,yín,圾:jī,jí,jié,址:zhǐ,坂:bǎn,均:jūn,yùn,
坉:tún,dùn,坊:fāng,fáng,坋:bèn,fèn,坌:bèn,坍:tān,坎:kǎn,kàn,坏:huài,pī,péi,坐:zuò,
坑:kēng,kàng,坒:bì,块:kuài,yué,坚:jiān,坛:tán,坜:lì,坝:bà,坞:wù,
坟:fén,坠:zhuì,坡:pō,坤:kūn,坥:qū,jù,坦:tǎn,坨:tuó,yí,坩:gān,
坪:píng,坫:diàn,zhēn,坬:guà,wā,坭:ní,坯:pī,huài,坰:jiōng,坳:ào,āo,yǒu,坷:kě,kē,jiōng,
坻:chí,dǐ,坼:chè,坽:líng,垂:chuí,zhuì,垃:lā,la,垄:lǒng,垆:lú,垈:dài,
型:xíng,垌:dòng,tóng,tǒng,垍:jì,jī,垎:hè,垏:lǜ,垒:lěi,垓:gāi,垕:hòu,
垙:guāng,垚:yáo,垛:duǒ,duò,垞:chá,垟:yáng,垠:yín,kèn,垡:fá,垢:gòu,
垣:yuán,垤:dié,垦:kěn,yín,垧:shǎng,jiōng,垩:è,shèng,垫:diàn,垭:yā,垮:kuǎ,
垯:da,dá,垱:dàng,垲:kǎi,垴:nǎo,垵:ǎn,ān,垸:yuàn,huán,垺:fū,póu,fú,fóu,pēi,垾:hàn,àn,
垿:xù,埂:gěng,埃:āi,zhì,埆:què,jué,埇:yǒng,埋:mái,mán,埌:làng,城:chéng,
埏:shān,yán,埒:liè,埔:pǔ,bù,埕:chéng,埗:bù,埘:shí,埙:xūn,埚:guō,
埝:niàn,diàn,niè,域:yù,埠:bù,埤:pí,pì,bì,bēi,埪:kōng,埫:chǒng,tǎng,shǎng,埭:dài,埯:ǎn,yǎn,
埴:zhí,埵:duǒ,埸:yì,培:péi,pǒu,pī,基:jī,埼:qí,埽:sào,sǎo,堂:táng,
堃:kūn,堆:duī,zuī,堇:jǐn,jīn,qín,jìn,堉:yù,堋:péng,bèng,pēng,pīng,堌:gù,堍:tù,堎:lèng,
堐:yá,堑:qiàn,堕:duò,huī,堙:yīn,堞:dié,堠:hòu,堡:bǎo,bǔ,pù,堤:dī,tí,dǐ,shí,wéi,
堧:ruán,nuò,堨:yè,è,ài,堪:kān,chěn,堰:yàn,堲:cí,jí,堵:dǔ,zhě,dū,堼:hèng,fēng,堽:gāng,
堾:chūn,chuǎn,塄:léng,塅:duàn,塆:wān,塌:tā,dā,塍:chéng,塑:sù,塔:tǎ,dā,da,
塘:táng,塝:bàng,塞:sāi,sài,sè,塥:gé,填:tián,tiǎn,chén,zhèn,塬:yuán,塱:lǎng,塾:shú,
墀:chí,墁:màn,境:jìng,墅:shù,yě,墈:kàn,墉:yōng,墐:jìn,qín,墒:shāng,
墓:mù,墕:yàn,yān,墘:qián,墙:qiáng,墚:liáng,增:zēng,zèng,céng,墟:xū,墡:shàn,
墣:pú,墦:fán,fān,墨:mò,mèi,墩:dūn,墼:jī,壁:bì,壅:yōng,wèng,壑:hè,huò,
壕:háo,壤:rǎng,士:shì,壬:rén,壮:zhuàng,声:shēng,qìng,壳:ké,qiào,壶:hú,
壸:kǔn,壹:yī,yīn,处:chù,chǔ,备:bèi,复:fù,夏:xià,jiǎ,夐:xiòng,xuàn,夔:kuí,
夕:xī,yì,外:wài,夙:sù,多:duō,夜:yè,够:gòu,夤:yín,夥:huǒ,
大:dà,dài,tài,天:tiān,太:tài,tā,夫:fū,fú,夬:guài,jué,夭:yāo,wò,wāi,央:yāng,yīng,夯:hāng,bèn,
失:shī,yì,头:tóu,tou,夷:yí,夸:kuā,kuà,kuǎ,夹:jiā,gā,jiá,夺:duó,夼:kuǎng,奁:lián,
奂:huàn,奄:yǎn,yān,奇:qí,jī,ǎi,yǐ,奈:nài,奉:fèng,奋:fèn,kǎng,奎:kuí,kuǐ,奏:zòu,còu,
契:qì,xiè,qiè,jié,奓:zhā,shē,chǐ,zhà,奔:bēn,bèn,fèn,奕:yì,奖:jiǎng,套:tào,tǎo,奘:zàng,zhuǎng,奚:xī,
奠:diàn,tíng,dìng,zhèng,zūn,奡:ào,xiào,奢:shē,奥:ào,yù,yōu,奭:shì,女:nǚ,nǜ,rǔ,奴:nú,奶:nǎi,
奸:jiān,gān,她:tā,jiě,chí,好:hǎo,hào,妁:shuò,yuē,如:rú,妃:fēi,pèi,妄:wàng,wáng,妆:zhuāng,
妇:fù,妈:mā,妊:rèn,rén,妍:yán,妒:dù,妓:jì,jī,妖:yāo,jiǎo,妗:jìn,xiān,
妘:yún,妙:miào,miǎo,妞:niū,hào,妣:bǐ,妤:yú,妥:tuǒ,妧:wàn,yuán,妨:fáng,fāng,
妩:wǔ,妪:yù,妫:guī,妭:bá,bō,妮:nī,ní,妯:zhóu,chōu,妲:dá,妹:mèi,
妻:qī,qì,妾:qiè,姆:mǔ,姈:líng,姊:zǐ,始:shǐ,姐:jiě,jù,xù,zū,姑:gū,
姒:sì,姓:xìng,shēng,委:wěi,wēi,wèi,姗:shān,姘:pīn,pín,姚:yáo,tiào,táo,yào,姜:jiāng,姝:shū,
姞:jí,姣:jiāo,jiǎo,xiáo,姤:gòu,姥:lǎo,mǔ,姨:yí,姬:jī,yí,姮:héng,姱:kuā,hù,
姶:è,yà,姹:chà,姻:yīn,姽:guǐ,wá,姿:zī,zì,娀:sōng,威:wēi,娃:wá,wā,guì,
娄:lóu,娅:yà,娆:ráo,rǎo,娇:jiāo,娈:luán,娉:pīng,pìn,娌:lǐ,娑:suō,suǒ,suò,
娓:wěi,娘:niáng,娜:nà,nuó,娟:juān,娠:shēn,娣:dì,娥:é,娩:miǎn,wǎn,wèn,
娱:yú,娲:wā,娴:xián,娵:jū,娶:qǔ,jū,shū,娼:chāng,婀:ē,ě,婆:pó,
婇:cǎi,婉:wǎn,婊:biǎo,婌:shú,shū,婍:qǐ,婕:jié,qiè,婘:quán,juàn,婚:hūn,
婞:xìng,婠:wān,guàn,婢:bì,婤:chōu,zhōu,婧:jìng,婪:lán,lǎn,婫:kūn,hùn,婳:huà,
婴:yīng,婵:chán,婶:shěn,婷:tíng,婺:wù,móu,mù,婻:nàn,婼:chuò,ruò,婿:xù,
媂:dì,tí,媄:měi,媆:ruǎn,nèn,nùn,媒:méi,mèi,媓:huáng,媖:yīng,媚:mèi,媛:yuàn,yuán,
媞:shì,tí,zhī,dài,媪:ǎo,yǔn,wò,媭:xū,媱:yáo,媲:pì,bī,pí,媳:xí,媵:yìng,shèng,媸:chī,
媾:gòu,嫁:jià,嫂:sǎo,嫄:yuán,嫉:jí,嫌:xián,嫒:ài,嫔:pín,
嫕:yì,嫖:piáo,piào,biāo,嫘:léi,嫚:mān,màn,yuān,嫜:zhāng,嫠:lí,嫡:dí,嫣:yān,
嫦:cháng,嫩:nèn,嫪:lào,láo,嫫:mó,嫭:hù,嫱:qiáng,嫽:liáo,liǎo,liào,lǎo,嬉:xī,xǐ,
嬖:bì,嬗:shàn,chán,嬛:huán,xuān,qióng,xuán,嬥:tiǎo,diào,嬬:rú,nòu,嬴:yíng,嬷:mā,mó,嬿:yàn,
孀:shuāng,孅:qiān,xiān,子:zi,zǐ,孑:jié,孓:jué,孔:kǒng,孕:yùn,孖:mā,zī,
字:zì,存:cún,孙:sūn,孚:fú,孛:bèi,bó,孜:zī,孝:xiào,孟:mèng,
孢:bāo,季:jì,孤:gū,孥:nú,学:xué,孩:hái,孪:luán,孬:nāo,
孰:shú,孱:càn,chán,jiān,zhàn,孳:zī,孵:fū,孺:rú,孽:niè,宁:níng,nìng,zhù,它:tā,tuó,yí,
宄:guǐ,宅:zhái,chè,dù,宇:yǔ,守:shǒu,shòu,安:ān,宋:sòng,完:wán,kuān,宏:hóng,
宓:mì,fú,宕:dàng,宗:zōng,官:guān,宙:zhòu,定:dìng,宛:wǎn,yuān,yǔn,yù,宜:yí,
宝:bǎo,实:shí,宠:chǒng,审:shěn,客:kè,qià,宣:xuān,室:shì,宥:yòu,
宦:huàn,宧:yí,宪:xiàn,xiòng,宫:gōng,宬:chéng,宰:zǎi,害:hài,hé,宴:yàn,
宵:xiāo,家:jiā,jia,jià,jie,gū,宸:chén,容:róng,yǒng,宽:kuān,宾:bīn,宿:sù,xiǔ,xiù,qī,寁:zǎn,
寂:jì,寄:jì,寅:yín,密:mì,寇:kòu,富:fù,寐:mèi,寒:hán,
寓:yù,寝:qǐn,寞:mò,察:chá,cuì,寡:guǎ,寤:wù,寥:liáo,寨:zhài,sè,qiān,
寮:liáo,寰:huán,xiàn,寸:cùn,cǔn,对:duì,寺:sì,shì,寻:xún,xín,导:dǎo,寿:shòu,
封:fēng,biǎn,射:shè,yè,yì,将:jiāng,jiàng,qiāng,尉:wèi,yù,yùn,尊:zūn,小:xiǎo,少:shǎo,shào,尔:ěr,
尕:gǎ,尖:jiān,尘:chén,尚:shàng,cháng,尜:gá,尝:cháng,尢:yóu,wāng,尤:yóu,
尥:liào,niǎo,尧:yáo,尨:máng,lóng,méng,páng,尪:wāng,尬:gà,就:jiù,尴:gān,尸:shī,
尹:yǐn,yún,尺:chǐ,chě,尻:kāo,尼:ní,nǐ,尽:jǐn,jìn,尾:wěi,yǐ,尿:niào,suī,局:jú,
屁:pì,层:céng,屃:xì,居:jū,jī,屈:qū,jué,què,jú,屉:tì,届:jiè,屋:wū,
屎:shǐ,xī,屏:píng,bǐng,bìng,bīng,屐:jī,屑:xiè,展:zhǎn,屙:ē,属:shǔ,zhǔ,屠:tú,
屡:lǚ,屣:xǐ,履:lǚ,屦:jù,屯:tún,zhūn,山:shān,屹:yì,gē,屺:qǐ,
屼:wù,屾:shēn,屿:yǔ,岁:suì,岂:qǐ,kǎi,岈:yá,xiā,岊:jié,岌:jí,
岍:qiān,岐:qí,岑:cén,岔:chà,岖:qū,岗:gǎng,gāng,岘:xiàn,岙:ào,
岚:lán,岛:dǎo,岜:bā,岞:zuò,岠:jù,岢:kě,岣:gǒu,岨:qū,jū,zǔ,jǔ,
岩:yán,岫:xiù,岬:jiǎ,jiá,岭:lǐng,líng,岱:dài,岳:yuè,岵:hù,岷:mín,
岸:àn,岽:dōng,岿:kuī,峁:mǎo,峂:tóng,峃:xué,峄:yì,峋:xún,
峒:dòng,tóng,峗:wéi,wěi,峘:huán,峙:zhì,shì,峛:lǐ,liè,峡:xiá,峣:yáo,峤:jiào,qiáo,
峥:zhēng,峦:luán,峧:jiāo,峨:é,峪:yù,峭:qiào,峰:fēng,峱:náo,
峻:jùn,峿:yǔ,wú,崀:làng,lǎng,崁:kàn,崂:láo,崃:lái,崄:xiǎn,崆:kōng,
崇:chóng,崌:jū,崎:qí,qǐ,yī,崒:zú,cuì,崔:cuī,崖:yá,崚:léng,líng,崛:jué,yù,
崞:guō,崟:yín,崡:hán,崤:xiáo,yáo,崦:yān,崧:sōng,崩:bēng,崭:zhǎn,
崮:gù,崴:wǎi,wēi,wěi,崶:fēng,崽:zǎi,崾:yǎo,yào,崿:è,嵁:kān,zhàn,嵅:hán,
嵇:jī,xí,嵊:shèng,chéng,嵋:méi,嵌:qiàn,hǎn,kàn,嵎:yú,嵖:chá,嵘:róng,嵚:qīn,
嵛:yú,嵝:lǒu,嵩:sōng,嵫:zī,嵬:wéi,wěi,嵯:cuó,cī,嵲:niè,嵴:jǐ,jí,
嶂:zhàng,嶅:áo,ào,嶍:xí,嶒:céng,zhēng,嶓:bō,嶙:lín,lǐn,嶝:dèng,嶟:zūn,
嶦:zhān,shàn,嶲:xī,guī,嶷:yí,nì,巅:diān,巇:xī,巉:chán,巍:wēi,川:chuān,
州:zhōu,巡:xún,yán,shùn,巢:cháo,chào,工:gōng,左:zuǒ,巧:qiǎo,巨:jù,qú,巩:gǒng,
巫:wū,差:chà,chā,chāi,cī,chài,cuō,jiē,巯:qiú,己:jǐ,qǐ,已:yǐ,sì,巳:sì,yǐ,巴:bā,巷:xiàng,hàng,
巽:xùn,zhuàn,巾:jīn,币:bì,yìn,市:shì,fú,布:bù,帅:shuài,帆:fān,fán,fàn,师:shī,
希:xī,帏:wéi,帐:zhàng,帑:tǎng,nú,帔:pèi,pī,帕:pà,mò,帖:tiē,tiè,tiě,帘:lián,chén,
帙:zhì,帚:zhǒu,帛:bó,帜:zhì,帝:dì,帡:píng,带:dài,帧:zhēn,zhèng,
帨:shuì,席:xí,帮:bāng,帱:chóu,dào,帷:wéi,常:cháng,帻:zé,帼:guó,
帽:mào,幂:mì,幄:wò,幅:fú,bī,幌:huǎng,幔:màn,幕:mù,màn,幖:biāo,
幛:zhàng,幞:fú,幡:fān,幢:chuáng,zhuàng,幪:méng,měng,干:gàn,gān,àn,平:píng,pián,bìng,bēng,年:nián,nìng,
并:bìng,bīng,幸:xìng,niè,幺:yāo,mì,幻:huàn,幼:yòu,yào,幽:yōu,广:guǎng,yǎn,ān,庄:zhuāng,péng,
庆:qìng,庇:bì,pí,pǐ,床:chuáng,庋:guǐ,guì,序:xù,庐:lú,庑:wǔ,库:kù,
应:yīng,yìng,底:dǐ,de,庖:páo,店:diàn,庙:miào,庚:gēng,府:fǔ,庞:páng,
废:fèi,庠:xiáng,庤:zhì,庥:xiū,度:dù,duó,zhái,座:zuò,庭:tíng,庱:chěng,
庳:bì,bēi,pí,庵:ān,yǎn,è,庶:shù,zhù,zhē,康:kāng,kàng,庸:yōng,yóng,庹:tuǒ,庼:qǐng,庾:yǔ,yú,
廆:guī,wěi,huì,廉:lián,廊:láng,廋:sōu,廑:jǐn,qín,廒:áo,廓:kuò,廖:liào,liáo,
廙:yì,廛:chán,廨:xiè,廪:lǐn,延:yán,廷:tíng,建:jiàn,廿:niàn,
开:kāi,弁:biàn,pán,异:yì,yí,弃:qì,弄:nòng,lòng,弆:jǔ,qǔ,弇:yǎn,yān,nán,弈:yì,
弊:bì,弋:yì,式:shì,tè,弑:shì,弓:gōng,引:yǐn,弗:fú,弘:hóng,
弛:chí,弟:dì,tì,tuí,张:zhāng,弢:tāo,弥:mí,弦:xián,弧:hú,弨:chāo,
弩:nǔ,弭:mǐ,弯:wān,弱:ruò,弶:jiàng,弸:péng,pēng,弹:dàn,tán,强:qiáng,jiàng,qiǎng,
弼:bì,彀:gòu,kōu,归:guī,当:dāng,dàng,录:lù,彖:tuàn,shǐ,彗:huì,suì,彘:zhì,
彝:yí,彟:yuē,huò,形:xíng,彤:tóng,彦:yàn,pán,彧:yù,彩:cǎi,彪:biāo,
彬:bīn,bān,彭:péng,páng,bāng,pēng,彯:piāo,piào,miǎo,彰:zhāng,影:yǐng,彳:chì,fú,彷:páng,fǎng,fáng,役:yì,
彻:chè,彼:bǐ,往:wǎng,wàng,征:zhēng,徂:cú,径:jìng,待:dài,dāi,徇:xùn,
很:hěn,徉:yáng,徊:huái,huí,律:lǜ,徐:xú,徒:tú,徕:lái,lài,得:dé,de,děi,
徘:pái,徙:xǐ,sī,徛:jì,徜:cháng,御:yù,yà,徨:huáng,循:xún,徭:yáo,
微:wēi,徵:zhēng,zhǐ,chéng,德:dé,徼:jiǎo,jiào,jiāo,yāo,徽:huī,心:xīn,必:bì,忆:yì,
忉:dāo,忌:jì,忍:rěn,rèn,忏:chàn,qiǎn,qiān,忐:tǎn,kěng,忑:tè,dǎo,忒:tè,tuī,tēi,忖:cǔn,
志:zhì,忘:wàng,wáng,忙:máng,忝:tiǎn,忞:mín,mǐn,wěn,忠:zhōng,忡:chōng,忤:wǔ,wù,
忧:yōu,yòu,忪:sōng,zhōng,快:kuài,忭:biàn,忮:zhì,qí,忱:chén,dàn,忳:tún,zhūn,dùn,念:niàn,
忸:niǔ,忺:xiān,忻:xīn,忽:hū,忾:kài,qì,忿:fèn,怀:huái,fù,态:tài,
怂:sǒng,怃:wǔ,怄:òu,怅:chàng,怆:chuàng,怊:chāo,怍:zuò,zhà,怎:zěn,
怏:yàng,yāng,怒:nù,怔:zhēng,zhèng,怕:pà,bó,怖:bù,怙:hù,tiē,怛:dá,dàn,怜:lián,líng,lǐng,
思:sī,sāi,怠:dài,yí,怡:yí,急:jí,怦:pēng,性:xìng,怨:yuàn,yùn,怩:ní,
怪:guài,怫:fú,fèi,bèi,怯:qiè,怵:chù,xù,总:zǒng,怼:duì,怿:yì,恁:nèn,rèn,nín,
恂:xún,shùn,恃:shì,zhì,恋:liàn,恍:huǎng,guāng,恐:kǒng,恒:héng,恓:xī,qī,xù,恔:jiǎo,xiào,
恕:shù,恙:yàng,恚:huì,恝:jiá,qì,恢:huī,恣:zì,恤:xù,恧:nǜ,
恨:hèn,恩:ēn,恪:kè,恫:dòng,tōng,恬:tián,恭:gōng,息:xī,恰:qià,
恳:kěn,恶:è,ě,wù,wū,恸:tòng,恹:yān,恺:kǎi,恻:cè,恼:nǎo,恽:yùn,
恿:yǒng,tōng,悃:kǔn,悄:qiāo,qiǎo,qiào,悆:yù,shū,悈:jiè,kè,悉:xī,悌:tì,悍:hàn,
悒:yì,悔:huǐ,悖:bèi,běi,悚:sǒng,悛:quān,xún,悝:kuī,lǐ,悟:wù,悠:yōu,
悢:liàng,lǎng,患:huàn,悦:yuè,您:nín,悫:què,悬:xuán,悭:qiān,悯:mǐn,
悰:cóng,悱:fěi,悲:bēi,悴:cuì,悷:lì,悸:jì,悻:xìng,悼:dào,
情:qíng,惆:chóu,qiū,dāo,惇:dūn,惊:jīng,liáng,惋:wǎn,惎:jì,惑:huò,惔:tán,dàn,
惕:tì,惘:wǎng,惙:chuò,chuì,惚:hū,惛:hūn,hǔn,mèn,惜:xī,惝:chǎng,tǎng,惟:wéi,wěi,
惠:huì,惦:diàn,惧:jù,惨:cǎn,惩:chéng,惫:bèi,惬:qiè,惭:cán,
惮:dàn,惯:guàn,惰:duò,tuó,想:xiǎng,惴:zhuì,chuǎn,guà,惶:huáng,惹:rě,ruò,惺:xīng,
愀:qiǎo,qiù,愁:chóu,qiǎo,jiū,愃:xuān,愆:qiān,愈:yù,愉:yú,tōu,yǔ,愍:mǐn,fēn,愎:bì,
意:yì,yī,愐:miǎn,愔:yīn,愕:è,愚:yú,感:gǎn,hàn,愠:yùn,yǔn,wěn,愣:lèng,
愤:fèn,愦:kuì,愧:kuì,愫:sù,愭:qí,愿:yuàn,慆:tāo,慈:cí,
慊:qiàn,qiè,xián,qiǎn,慌:huāng,huǎng,huang,慎:shèn,zhèn,慑:shè,慕:mù,慝:tè,nì,慢:màn,mán,慥:zào,cào,
慧:huì,慨:kǎi,慬:qín,jìn,jǐn,慭:yìn,慰:wèi,慵:yōng,慷:kāng,憋:biē,
憎:zēng,憔:qiáo,憕:chéng,zhèng,dèng,憙:xī,xǐ,憧:chōng,zhuàng,憨:hān,憩:qì,憬:jǐng,
憭:liǎo,liáo,憷:chù,chǔ,憺:dàn,憾:hàn,dàn,懂:dǒng,懈:xiè,懊:ào,yù,懋:mào,
懑:mèn,懒:lǎn,懔:lǐn,lǎn,懦:nuò,懵:měng,mèng,懿:yì,yī,戆:gàng,zhuàng,戈:gē,
戊:wù,戋:jiān,戌:xū,qu,戍:shù,戎:róng,rēng,戏:xì,hū,成:chéng,我:wǒ,
戒:jiè,戕:qiāng,zāng,或:huò,yù,戗:qiāng,qiàng,战:zhàn,戚:qī,cù,戛:jiá,gā,戟:jǐ,
戡:kān,zhěn,戢:jí,戣:kuí,戤:gài,戥:děng,截:jié,戬:jiǎn,戭:yǎn,yǒu,
戮:lù,戳:chuō,戴:dài,户:hù,戽:hù,戾:lì,房:fáng,páng,所:suǒ,
扁:biǎn,piān,biān,pián,扂:diàn,扃:jiōng,jiǒng,扅:yí,扆:yǐ,扇:shàn,shān,扈:hù,扉:fēi,
扊:yǎn,手:shǒu,才:cái,zāi,扎:zhā,zā,zhá,zhǎ,扑:pū,pì,扒:bā,pá,bài,bié,打:dǎ,dá,扔:rēng,rèng,
托:tuō,扛:káng,gāng,扞:gǎn,hàn,扣:kòu,扦:qiān,执:zhí,扩:kuò,扪:mén,
扫:sǎo,sào,扬:yáng,扭:niǔ,chǒu,zhǒu,zhòu,扮:bàn,fěn,fēn,huǒ,扯:chě,扰:rǎo,yòu,扳:bān,pān,扶:fú,pú,
批:pī,pí,扺:zhǐ,qí,扼:è,扽:dèn,找:zhǎo,huá,承:chéng,zhěng,zhèng,技:jì,qí,抃:biàn,
抄:chāo,suō,chào,chǎo,抉:jué,把:bǎ,bà,pá,抑:yì,抒:shū,抓:zhuā,抔:póu,投:tóu,dòu,
抖:dǒu,抗:kàng,gāng,折:zhé,zhē,shé,tí,抚:fǔ,抛:pāo,抟:tuán,抠:kōu,抡:lūn,lún,
抢:qiǎng,qiāng,护:hù,报:bào,抨:pēng,bēng,披:pī,抬:tái,chī,抱:bào,pāo,pǒu,抵:dǐ,zhǐ,qí,
抹:mǒ,mā,mò,抻:chēn,shēn,押:yā,xiá,jiǎ,抽:chōu,抿:mǐn,拂:fú,bì,pì,fèi,拃:zhǎ,zhǎn,zhà,zhá,拄:zhǔ,
担:dān,dàn,dǎn,jiē,拆:chāi,chè,chì,cā,拇:mǔ,拈:niān,niǎn,diān,拉:lā,lá,lǎ,là,la,拊:fǔ,fū,bǔ,拌:bàn,pān,拍:pāi,bó,
拎:līn,līng,拐:guǎi,拒:jù,jǔ,拓:tuò,tà,zhí,拔:bá,bō,bié,fá,bèi,拖:tuō,chǐ,拗:ǎo,ào,niù,yù,拘:jū,gōu,jǔ,jú,
拙:zhuō,招:zhāo,qiáo,sháo,拜:bài,bái,拟:nǐ,拢:lǒng,拣:jiǎn,拤:qiá,qiǎ,拥:yōng,
拦:lán,拧:níng,nǐng,nìng,拨:bō,择:zé,zhái,括:kuò,guā,拭:shì,拮:jié,jiá,拯:zhěng,
拱:gǒng,jú,拳:quán,拴:shuān,quán,拶:zā,zǎn,拷:kǎo,拼:pīn,bìng,拽:zhuāi,zhuài,yè,拾:shí,shè,jiè,
拿:ná,持:chí,挂:guà,指:zhǐ,zhī,zhí,挈:qiè,qì,jiá,qià,shì,按:àn,挎:kuà,kū,kōu,挑:tiāo,tiǎo,táo,diào,tiáo,tiao,
挓:zhā,挖:wā,挚:zhì,挛:luán,挝:wō,zhuā,挞:tà,挟:xié,jiā,挠:náo,
挡:dǎng,dàng,挣:zhēng,zhèng,挤:jǐ,挥:huī,挦:xián,挨:āi,ái,挪:nuó,挫:cuò,zuò,
振:zhèn,zhēn,zhěn,挲:sā,suō,shā,挹:yì,挺:tǐng,tíng,挽:wǎn,捂:wǔ,wú,捃:jùn,捅:tǒng,
捆:kǔn,hún,捉:zhuō,捋:lǚ,luō,捌:bā,bié,捍:hàn,xiàn,gǎn,捎:shāo,shào,shǎo,xiāo,qiào,捏:niē,捐:juān,yuán,
捕:bǔ,捞:lāo,损:sǔn,捡:jiǎn,换:huàn,捣:dǎo,捧:pěng,fèng,捩:liè,lì,
捭:bǎi,bā,bǐ,据:jù,jū,捯:dáo,dǎo,捶:chuí,duǒ,捷:jié,qiè,chā,捺:nà,捻:niǎn,niē,niān,捽:zuó,cù,sū,zùn,
掀:xiān,hén,掂:diān,掇:duō,duó,zhuō,授:shòu,掉:diào,nuó,掊:póu,pǒu,fù,péi,掌:zhǎng,掎:jǐ,yǐ,
掏:tāo,táo,掐:qiā,排:pái,pǎi,bài,掖:yē,yè,掘:jué,kū,掞:shàn,yàn,yǎn,掠:lüè,lüě,探:tàn,xián,
掣:chè,接:jiē,xié,shà,chā,控:kòng,kōng,qiāng,推:tuī,掩:yǎn,yàn,措:cuò,zé,cì,掬:jū,掭:tiàn,
掮:qián,掰:bāi,掳:lǔ,掴:guāi,guó,掷:zhì,zhī,掸:dǎn,shàn,掺:càn,chān,shǎn,掼:guàn,
掾:yuàn,chuán,揄:yú,chōu,yóu,shū,yáo,揆:kuí,揉:róu,揍:zòu,còu,描:miáo,mào,提:tí,dī,chí,shí,dǐ,插:chā,zhǎ,
揕:zhèn,zhēn,揖:yī,jí,揠:yà,握:wò,òu,揣:chuāi,chuǎi,chuài,duǒ,zhuī,tuán,揩:kāi,jiá,揪:jiū,揭:jiē,qì,hé,
揳:xiē,xiè,xié,jiá,援:yuán,huàn,揶:yé,揸:zhā,揽:lǎn,揿:qìn,搀:chān,搁:gē,gé,
搂:lǒu,lōu,搅:jiǎo,搋:chuāi,chǐ,yí,搌:zhǎn,搏:bó,搐:chù,搒:bàng,péng,bēng,bǎng,搓:cuō,cuǒ,chāi,
搔:sāo,sào,搛:jiān,lián,搜:sōu,xiāo,sòu,shǎo,搞:gǎo,qiāo,kào,搠:shuò,搡:sǎng,搦:nuò,搪:táng,
搬:bān,sù,搭:dā,tà,搴:qiān,携:xié,搽:chá,摁:èn,摄:shè,摅:shū,
摆:bǎi,摇:yáo,摈:bìn,摊:tān,摏:chōng,摒:bǐng,bìng,摔:shuāi,摘:zhāi,
摛:chī,摞:luò,摧:cuī,zuì,cuò,摩:mó,mā,mí,摭:zhí,摴:chū,chī,摸:mō,mó,摹:mó,mō,
摽:biāo,biào,piāo,pāo,撂:liào,撄:yīng,撅:juē,juè,jué,guì,撇:piē,piě,biē,撑:chēng,撒:sā,sǎ,撕:sī,xī,
撖:hàn,qiǎn,撙:zǔn,撞:zhuàng,撤:chè,撩:liāo,liáo,liǎo,lào,liào,撬:qiào,播:bō,bǒ,撮:cuō,zuǒ,zuì,zuān,chuā,
撰:zhuàn,xuǎn,suàn,撵:niǎn,撷:xié,撸:lū,撺:cuān,撼:hàn,擀:gǎn,擂:léi,lèi,lēi,
擅:shàn,操:cāo,擎:qíng,擐:huàn,juǎn,xuān,擒:qín,擘:bāi,bò,擞:sǒu,sòu,擢:zhuó,
擤:xǐng,擦:cā,擿:tī,zhì,zhāi,攀:pān,攉:huō,huò,què,攒:zǎn,cuán,攘:rǎng,ràng,níng,xiǎng,攥:zuàn,
攫:jué,攮:nǎng,支:zhī,zhì,qí,收:shōu,攸:yōu,改:gǎi,攻:gōng,攽:bān,bīn,
放:fàng,fǎng,fāng,政:zhèng,zhēng,故:gù,效:xiào,敉:mǐ,敌:dí,huá,敏:mǐn,救:jiù,jiū,
敔:yǔ,yù,敕:chì,sōu,敖:áo,ào,教:jiào,jiāo,敛:liǎn,敝:bì,敞:chǎng,chèng,zhèng,敢:gǎn,
散:sàn,sǎn,sān,敦:dūn,duī,tuán,diāo,dùn,dào,zhǔn,tūn,duì,tún,敩:xiào,xué,敫:jiǎo,qiāo,jiào,敬:jìng,数:shù,shǔ,shuò,敲:qiāo,整:zhěng,
敷:fū,文:wén,斋:zhāi,斌:bīn,斐:fěi,斑:bān,斓:lán,斗:dòu,dǒu,zhǔ,
料:liào,liáo,斛:hú,斜:xié,xiá,chá,yé,斝:jiǎ,斟:zhēn,斠:jiào,斡:wò,guǎn,斤:jīn,
斥:chì,chè,zhè,斧:fǔ,斩:zhǎn,斫:zhuó,chuò,断:duàn,斯:sī,shǐ,新:xīn,斶:chù,
方:fāng,fáng,fǎng,páng,wǎng,fēng,於:yú,yū,wū,施:shī,yì,shǐ,旁:páng,pēng,bēng,bàng,旃:zhān,旄:máo,mào,wù,旅:lǚ,旆:pèi,
旋:xuán,xuàn,旌:jīng,旎:nǐ,族:zú,sǒu,còu,zòu,旐:zhào,旒:liú,旖:yǐ,旗:qí,
旞:suì,无:wú,mó,既:jì,xì,日:rì,旦:dàn,旧:jiù,旨:zhǐ,早:zǎo,
旬:xún,jūn,旭:xù,旮:gā,xù,旯:lá,旰:gàn,hàn,旱:hàn,旴:xū,xù,旵:chǎn,
时:shí,旷:kuàng,旸:yáng,旺:wàng,旻:mín,旽:tūn,zhùn,旿:wǔ,wù,昀:yún,
昂:áng,yàng,昃:zè,昄:bǎn,昆:kūn,hún,kùn,昇:shēng,昈:hù,昉:fǎng,昊:hào,
昌:chāng,chàng,明:míng,mèng,昏:hūn,hùn,昒:hū,易:yì,昔:xī,cuò,昕:xīn,xuān,昙:tán,yù,
昝:zǎn,星:xīng,映:yìng,yǎng,昡:xuàn,昣:zhěn,昤:líng,春:chūn,chǔn,昧:mèi,wěn,mò,
昨:zuó,昪:biàn,昫:xù,xiǒng,昭:zhāo,zhào,是:shì,tí,昱:yù,昳:dié,diè,yì,昴:mǎo,
昵:nì,nǐ,zhì,昶:chǎng,昺:bǐng,昼:zhòu,昽:lóng,显:xiǎn,晁:cháo,zhāo,chào,晃:huǎng,huàng,
晅:xuǎn,xuān,晊:zhì,晋:jìn,晌:shǎng,晏:yàn,晐:gāi,晒:shài,晓:xiǎo,
晔:yè,晕:yūn,yùn,晖:huī,晗:hán,晙:jùn,晚:wǎn,晞:xī,晟:chéng,shèng,jīng,
晡:bū,晢:zhé,zhì,晤:wù,晦:huì,晨:chén,晪:tiǎn,晫:zhuó,普:pǔ,
景:jǐng,yǐng,晰:xī,晱:shǎn,晴:qíng,晶:jīng,晷:guǐ,智:zhì,zhī,晾:liàng,
暂:zàn,暄:xuān,暅:gèng,xuǎn,暇:xiá,xià,jiǎ,暌:kuí,暑:shǔ,暕:jiǎn,lán,暖:nuǎn,xuān,
暗:àn,暝:míng,暧:ài,nuǎn,暨:jì,jiè,暮:mù,暲:zhāng,暴:bào,pù,bó,暵:hàn,
暶:xuán,暹:xiān,暾:tūn,暿:xǐ,xī,曈:tóng,曌:zhào,曙:shǔ,曛:xūn,
曜:yào,曝:pù,bào,曦:xī,曩:nǎng,曰:yuē,曲:qū,qǔ,曳:yè,更:gèng,gēng,
曷:hé,è,hè,曹:cáo,曼:màn,曾:céng,zēng,替:tì,最:zuì,cuō,月:yuè,rù,有:yǒu,yòu,wěi,
朋:péng,服:fú,fù,bì,bó,朏:fěi,kū,朐:qú,xū,xù,chǔn,朓:tiǎo,tiào,yóu,朔:shuò,朕:zhèn,朗:lǎng,
望:wàng,朝:cháo,zhāo,zhū,期:qī,jī,朦:méng,mǎng,木:mù,未:wèi,末:mò,me,本:běn,bēn,
札:zhá,yà,术:shù,zhú,shú,朱:zhū,shū,朳:bā,朴:pǔ,piáo,pò,pū,pō,朵:duǒ,朸:lì,机:jī,wèi,
朽:xiǔ,杀:shā,杂:zá,duǒ,权:quán,杄:qiān,杆:gān,gǎn,gàn,杈:chā,chà,杉:shān,shā,
杌:wù,wò,李:lǐ,杏:xìng,材:cái,村:cūn,杓:biāo,sháo,shuó,dí,zhuó,杕:dì,duò,杖:zhàng,
杙:yì,杜:dù,dǔ,tú,杞:qǐ,束:shù,杠:gāng,gàng,gōng,条:tiáo,来:lái,杧:máng,
杨:yáng,杩:mà,杪:miǎo,杭:háng,kàng,kāng,杯:bēi,杰:jié,杲:gǎo,杳:yǎo,
杵:chǔ,杷:pá,bà,杻:chǒu,niǔ,杼:zhù,shù,松:sōng,板:bǎn,极:jí,构:gòu,
枅:jī,枇:pí,bǐ,bì,pī,枉:wǎng,kuáng,枋:fāng,fǎng,bǐng,枍:yì,析:xī,sī,枕:zhěn,chén,林:lín,
枘:ruì,nèn,枚:méi,果:guǒ,luǒ,guàn,枝:zhī,qí,枞:cōng,zōng,枢:shū,枣:zǎo,枥:lì,
枧:jiǎn,枨:chéng,枪:qiāng,枫:fēng,枭:xiāo,枯:kū,gū,枰:píng,枲:xǐ,
枳:zhǐ,zhī,枵:xiāo,架:jià,枷:jiā,jià,枸:gǒu,jǔ,gōu,qú,枹:bāo,fú,柁:duò,tuó,tuǒ,柃:líng,
柄:bǐng,柈:bàn,pán,pàn,柊:zhōng,柏:bǎi,bó,bò,某:mǒu,méi,柑:gān,qián,柒:qī,染:rǎn,
柔:róu,柖:sháo,shào,柘:zhè,柙:xiá,jiǎ,柚:yòu,yóu,zhóu,柜:guì,jǔ,柝:tuò,柞:zhà,zuò,zé,
柠:níng,chǔ,zhù,柢:dǐ,dì,chí,查:chá,zhā,chái,柩:jiù,柬:jiǎn,柯:kē,柰:nài,柱:zhù,zhǔ,
柳:liǔ,柴:chái,cī,zhài,zì,柷:chù,zhù,柽:chēng,jué,柿:shì,栀:zhī,栅:zhà,shān,cè,标:biāo,
栈:zhàn,栉:zhì,栊:lóng,栋:dòng,栌:lú,栎:lì,yuè,栏:lán,栐:yǒng,
树:shù,栒:xún,sǔn,栓:shuān,shuàn,quán,栖:qī,xī,栗:lì,liè,栝:guā,tiǎn,kuò,栟:bēn,bīng,校:xiào,jiào,jiǎo,qiāo,
栩:xǔ,yǔ,株:zhū,栲:kǎo,栳:lǎo,栴:zhān,样:yàng,yáng,核:hé,hú,gāi,kài,根:gēn,
栻:shì,格:gé,luò,hè,gē,栽:zāi,zài,栾:luán,桀:jié,桁:héng,háng,hàng,桂:guì,桃:táo,tiāo,zhào,
桄:guāng,guàng,桅:wéi,guǐ,框:kuāng,kuàng,kuáng,案:àn,桉:ān,àn,桊:juàn,quān,桌:zhuō,桎:zhì,
桐:tóng,tōng,dòng,桑:sāng,桓:huán,桔:jú,jié,xié,桕:jiù,桠:yā,桡:ráo,桢:zhēn,
档:dàng,桤:qī,桥:qiáo,桦:huà,桧:guì,huì,桨:jiǎng,桩:zhuāng,桫:suō,
桯:tīng,yíng,桲:po,bó,桴:fú,桶:tǒng,桷:jué,桹:láng,梁:liáng,梃:tǐng,tìng,
梅:méi,梆:bāng,梌:tú,chá,tū,梏:gù,jué,梓:zǐ,梗:gěng,梠:lǚ,梢:shāo,shào,xiāo,sào,
梣:cén,chén,qín,梦:mèng,梧:wú,wù,yǔ,梨:lí,梭:suō,xùn,梯:tī,tí,械:xiè,梳:shū,
梴:chān,梵:fàn,梼:táo,chóu,梽:zhì,梾:lái,梿:lián,检:jiǎn,棁:zhuō,tuō,ruì,
棂:líng,棉:mián,棋:qí,jī,棍:gùn,hùn,āo,gǔn,棐:fěi,féi,棒:bàng,棓:bàng,bèi,pǒu,péi,bēi,棕:zōng,
棘:jí,棚:péng,棠:táng,棣:dì,tì,dài,棤:cuò,què,棨:qǐ,棪:yǎn,yàn,棫:yù,
棬:quān,juàn,quán,森:sēn,棰:chuí,duǒ,棱:léng,lēng,líng,lèng,chēng,棵:kē,kuǎn,kě,棹:zhào,zhuō,棺:guān,guàn,棻:fēn,
棼:fén,fèn,fēn,棽:shēn,chēn,椀:wǎn,椁:guǒ,椅:yǐ,yī,椆:chóu,zhòu,diāo,椋:liáng,植:zhí,
椎:chuí,zhuī,椐:jū,椑:bēi,pí,bì,pái,椒:jiāo,椓:zhuó,椟:dú,椠:qiàn,椤:luó,
椪:pèng,椭:tuǒ,椰:yē,椴:duàn,椸:yí,椹:shèn,zhēn,椽:chuán,椿:chūn,
楂:zhā,chá,楒:sī,楔:xiē,xiè,楗:jiàn,jiǎn,楙:mào,máo,楚:chǔ,楝:liàn,楞:léng,lèng,
楠:nán,楣:méi,měi,楦:xuàn,楩:pián,楪:yè,dié,楫:jí,楮:chǔ,zhū,楯:dùn,shǔn,chūn,
楷:kǎi,jiē,jiè,楸:qiū,楹:yíng,楼:lóu,概:gài,guì,jié,榃:tán,榄:lǎn,榅:wēn,yùn,
榆:yú,榇:chèn,榈:lǘ,榉:jǔ,榍:xiè,榑:fú,fù,bó,榔:láng,lǎng,榕:róng,
榖:gǔ,榛:zhēn,榜:bǎng,bēng,bàng,páng,péng,榧:fěi,榨:zhà,榫:sǔn,榭:xiè,榰:zhī,
榱:cuī,榴:liú,榷:què,榻:tà,槁:gǎo,kào,gāo,槃:pán,槊:shuò,槌:chuí,zhuì,duī,
槎:chá,槐:huái,槔:gāo,槚:jiǎ,槛:kǎn,jiàn,槜:zuì,槟:bīn,bīng,槠:zhū,
槭:qī,qì,cù,zú,sè,槱:yǒu,chǎo,槲:hú,槽:cáo,zāo,槿:jǐn,qín,樊:fán,fàn,樋:tōng,樗:chū,
樘:táng,chēng,樟:zhāng,模:mó,mú,樨:xī,横:héng,hèng,guāng,guàng,huáng,huàng,樯:qiáng,樱:yīng,樵:qiáo,
樽:zūn,樾:yuè,橄:gǎn,橇:qiāo,橐:tuó,dù,luò,橑:lǎo,liáo,橘:jú,橙:chéng,dèng,chén,
橛:jué,橞:huì,橡:xiàng,橥:zhū,橦:tóng,chuáng,zhōng,chōng,橱:chú,橹:lǔ,橼:yuán,
檀:tán,shàn,檄:xí,檎:qín,檐:yán,dān,檑:léi,lèi,檗:bò,bì,檞:jiě,xiè,檠:qíng,jìng,
檩:lǐn,檫:chá,sà,檬:méng,櫆:kuí,櫚:lǘ,欂:bó,欠:qiàn,次:cì,zī,cí,
欢:huān,欣:xīn,欤:yú,欧:ōu,欲:yù,欸:āi,èi,ǎi,ế,ề,xiè,éi,ěi,ēi,欹:yī,qī,欺:qī,
欻:chuā,xū,hū,款:kuǎn,xīn,歃:shà,xiá,歅:yīn,yān,歆:xīn,歇:xiē,yà,歉:qiàn,歌:gē,
歙:shè,xī,xié,止:zhǐ,正:zhèng,zhēng,此:cǐ,步:bù,武:wǔ,歧:qí,歪:wāi,wǎi,
歹:dǎi,è,dāi,死:sǐ,歼:jiān,殁:mò,wěn,殂:cú,殃:yāng,殄:tiǎn,殆:dài,
殇:shāng,殉:xùn,殊:shū,残:cán,殍:piǎo,bì,殒:yǔn,殓:liàn,殖:zhí,shi,shì,
殚:dān,殛:jí,殡:bìn,殣:jìn,殪:yì,殳:shū,殴:ōu,段:duàn,
殷:yīn,yān,yǐn,殿:diàn,毁:huǐ,huì,毂:gǔ,gū,毅:yì,毋:wú,móu,毌:guàn,母:mǔ,mú,wǔ,wú,
每:měi,毐:ǎi,毒:dú,dài,毓:yù,比:bǐ,bì,pí,pǐ,毕:bì,毖:bì,毗:pí,
毙:bì,毛:máo,mào,毡:zhān,毪:mú,毫:háo,毯:tǎn,毳:cuì,qiāo,xiā,毵:sān,
毹:shū,yú,毽:jiàn,氅:chǎng,氆:pǔ,氇:lu,lǔ,氍:qú,氏:shì,zhī,jīng,氐:dī,dǐ,zhī,
民:mín,氓:máng,méng,气:qì,qǐ,氕:piē,氖:nǎi,氘:dāo,氙:xiān,氚:chuān,
氛:fēn,氟:fú,氡:dōng,氢:qīng,氤:yīn,yán,氦:hài,氧:yǎng,氨:ān,
氩:yà,氪:kè,氮:dàn,氯:lǜ,氰:qíng,氲:yūn,yún,水:shuǐ,永:yǒng,
氾:fàn,fán,氿:guǐ,jiǔ,qiú,汀:tīng,tìng,dìng,汁:zhī,xié,shí,求:qiú,汆:cuān,汇:huì,汈:diāo,
汉:hàn,汊:chà,汋:zhuó,yuè,què,shuò,汐:xī,汔:qì,汕:shàn,shuàn,汗:hàn,hán,gān,汛:xùn,
汜:sì,汝:rǔ,汞:gǒng,江:jiāng,池:chí,tuó,chè,污:wū,汤:tāng,shāng,汧:qiān,yán,
汨:mì,汩:gǔ,yù,hú,汪:wāng,wǎng,hóng,汫:jǐng,汭:ruì,tūn,汰:tài,汲:jí,jī,汴:biàn,
汶:wèn,wén,mín,mén,汹:xiōng,汽:qì,gài,yǐ,汾:fén,pén,fēn,沁:qìn,沂:yí,yín,沃:wò,沄:yún,
沅:yuán,沆:hàng,háng,kàng,沇:yǎn,wěi,沈:shěn,chén,tán,沉:chén,沌:dùn,zhuàn,tún,chún,沏:qī,qiè,沐:mù,
沓:dá,tà,沔:miǎn,沘:bǐ,沙:shā,shà,suō,沚:zhǐ,沛:pèi,沟:gōu,没:méi,mò,me,
沣:fēng,沤:ōu,òu,沥:lì,沦:lún,沧:cāng,沨:fēng,沩:wéi,沪:hù,
沫:mò,沭:shù,沮:jǔ,jū,jù,jiān,zǔ,沱:tuó,duò,chí,河:hé,沸:fèi,fú,油:yóu,yòu,沺:tián,
治:zhì,chí,沼:zhǎo,沽:gū,gǔ,沾:zhān,tiān,diàn,chān,沿:yán,yǎn,yàn,泂:jiǒng,yíng,yǐng,jiōng,泃:jū,gōu,泄:xiè,yì,
泅:qiú,yōu,泇:jiā,泉:quán,泊:pō,bó,pò,泌:mì,bì,泐:lè,泓:hóng,泔:gān,hàn,
法:fǎ,泖:mǎo,liǔ,泗:sì,泙:píng,pēng,泚:cǐ,zǐ,泛:fàn,fěng,fá,泜:zhī,chí,zhì,泞:nìng,zhù,
泠:líng,lǐng,泡:pào,pāo,páo,波:bō,bēi,bì,泣:qì,lì,sè,泥:ní,nì,nǐ,niè,nìng,注:zhù,zhòu,泪:lèi,泫:xuàn,xuán,juān,
泮:pàn,泯:mǐn,miàn,泰:tài,泱:yāng,泳:yǒng,泵:bèng,pìn,liú,泷:lóng,shuāng,泸:lú,
泺:luò,pō,泻:xiè,泼:pō,泽:zé,泾:jīng,洁:jié,jí,洄:huí,huì,洇:yīn,yān,yē,
洈:wéi,洋:yáng,xiáng,yǎng,洌:liè,洎:jì,洑:fú,fù,洒:sǎ,xǐ,xiǎn,sěn,cuǐ,xùn,洓:sè,qì,suǒ,zì,洗:xǐ,xiǎn,
洘:kǎo,kào,洙:zhū,洚:jiàng,hóng,洛:luò,洞:dòng,tóng,洢:yī,洣:mǐ,津:jīn,
洧:wěi,洨:xiáo,洪:hóng,洫:xù,yì,洭:kuāng,洮:táo,yáo,dào,洱:ěr,洲:zhōu,
洳:rù,rú,洴:píng,pēng,洵:xún,xuàn,洸:guāng,huàng,洹:huán,洺:míng,活:huó,guō,洼:wā,guī,
洽:qià,hé,派:pài,mài,bài,pā,洿:wū,hù,流:liú,浃:jiā,浅:qiǎn,jiān,浆:jiāng,jiàng,浇:jiāo,
浈:zhēn,浉:shī,浊:zhuó,测:cè,浍:huì,kuài,济:jì,jǐ,浏:liú,浐:chǎn,
浑:hún,浒:hǔ,xǔ,浓:nóng,浔:xún,浕:jìn,浙:zhè,浚:jùn,xùn,cún,浛:hán,hàn,gān,
浜:bāng,bīn,浞:zhuó,浟:yóu,yōu,dí,浠:xī,浡:bó,浣:huàn,浥:yì,yà,浦:pǔ,
浩:hào,gǎo,gé,浪:làng,láng,浬:lǐ,hǎi,浭:gēng,浮:fú,浯:wú,浰:liàn,lì,浲:féng,hóng,
浴:yù,海:hǎi,浸:jìn,qīn,浼:měi,涂:tú,chú,yé,涄:pīng,涅:niè,消:xiāo,
涉:shè,dié,涌:yǒng,chōng,涍:xiào,涎:xián,yàn,diàn,涐:é,涑:sù,sōu,shù,涓:juān,yuàn,xuàn,涔:cén,qián,zàn,
涕:tì,涘:sì,涛:tāo,涝:lào,涞:lái,涟:lián,涠:wéi,涡:wō,guō,
涢:yún,涣:huàn,huì,涤:dí,润:rùn,涧:jiàn,涨:zhǎng,zhàng,涩:sè,涪:fú,póu,
涫:guàn,guān,涮:shuàn,shuā,涯:yá,液:yè,shì,涴:wò,wǎn,yuān,涵:hán,hàn,涸:hé,涿:zhuō,zhuó,
淀:diàn,淄:zī,淅:xī,淆:xiáo,淇:qí,淋:lín,lìn,淌:tǎng,chàng,chǎng,淏:hào,
淑:shū,chù,淖:nào,zhào,zhuō,chuò,淘:táo,淙:cóng,shuàng,淜:píng,péng,淝:féi,淞:sōng,淟:tiǎn,
淠:pì,pèi,淡:dàn,yàn,tán,淤:yū,淦:gàn,hán,淫:yín,yàn,yáo,淬:cuì,zú,淮:huái,淯:yù,
深:shēn,淳:chún,zhūn,zhǔn,淴:hū,混:hùn,gǔn,hún,kūn,淹:yān,yǎn,添:tiān,tiàn,淼:miǎo,清:qīng,qìng,
渊:yuān,渌:lù,渍:zì,渎:dú,渐:jiàn,jiān,渑:miǎn,shéng,渔:yú,渗:shèn,
渚:zhǔ,渝:yú,yū,渟:tíng,tīng,渠:qú,jù,渡:dù,渣:zhā,渤:bó,渥:wò,òu,wū,
温:wēn,yùn,渫:xiè,dié,zhá,yì,qiè,渭:wèi,港:gǎng,hòng,渰:yǎn,yān,渲:xuàn,渴:kě,jié,kài,hé,游:yóu,liú,
渺:miǎo,渼:měi,湃:pài,bá,湄:méi,湉:tián,湍:tuān,zhuān,湎:miǎn,湑:xū,xǔ,xù,
湓:pén,pèn,湔:jiān,zàn,zhǎn,qián,jiàn,湖:hú,湘:xiāng,湛:zhàn,chén,dān,tán,jìn,yǐn,chěn,yín,shèn,湜:shí,湝:jiē,xié,湞:zhēn,chēng,
湟:huáng,kuàng,湣:mǐn,hūn,miàn,湫:jiǎo,qiū,jiù,jiū,jiāo,湮:yān,yīn,湲:yuán,湴:bàn,pán,湾:wān,湿:shī,
溁:yíng,溃:kuì,huì,溅:jiàn,jiān,溆:xù,溇:lóu,溉:gài,xiè,溍:jìn,溏:táng,
源:yuán,溘:kè,kài,溚:tǎ,dá,溜:liū,liù,liú,溞:sāo,溟:míng,mǐng,mì,溠:zhà,zhā,溢:yì,
溥:pǔ,fū,bù,bó,pò,溦:wēi,méi,溧:lì,溪:xī,qī,溯:sù,shuò,溱:qín,zhēn,溲:sōu,sǒu,shāo,溴:xiù,chòu,
溵:yīn,溶:róng,溷:hùn,hún,溹:suò,suǒ,sè,溺:nì,ruò,niào,溻:tā,溽:rù,rú,滁:chú,
滂:pāng,pēng,滃:wēng,wěng,滆:gé,滇:diān,tián,zhēn,滉:huàng,滋:zī,cí,xuán,滍:zhì,滏:fǔ,
滑:huá,gǔ,滓:zǐ,滔:tāo,滕:téng,滗:bì,滘:jiào,滚:gǔn,滞:zhì,
滟:yàn,滠:shè,满:mǎn,滢:yíng,滤:lǜ,滥:làn,滦:luán,滧:yáo,xiào,
滨:bīn,滩:tān,滪:yù,滫:xiǔ,滴:dī,滹:hū,hǔ,漂:piāo,piào,piǎo,biāo,漆:qī,qiè,
漈:jì,漉:lù,漋:lóng,漏:lòu,lóu,漓:lí,演:yǎn,yàn,漕:cáo,cào,漖:jiào,
漠:mò,漤:lǎn,漦:chí,tāi,漩:xuán,漪:yī,漫:màn,漭:mǎng,漯:luò,tà,lěi,
漱:shù,漳:zhāng,漴:zhuàng,chóng,shuāng,chuáng,漶:huàn,漷:huǒ,kuò,huò,漹:yān,漻:liáo,xiào,liú,漼:cuǐ,cuī,
漾:yàng,潆:yíng,潇:xiāo,潋:liàn,潍:wéi,潏:yù,jué,shù,潖:pá,潘:pān,pàn,bō,pán,fān,
潜:qián,潞:lù,潟:xì,潢:huáng,huàng,guāng,潦:lǎo,liáo,lào,láo,liǎo,潩:yì,潭:tán,xún,yǐn,dàn,潮:cháo,
潲:shào,潴:zhū,潵:sǎ,sàn,潸:shān,潺:chán,潼:tóng,chōng,zhōng,潽:pū,pǔ,潾:lín,
澂:chéng,澄:chéng,dèng,澈:chè,澉:gǎn,hàn,澌:sī,澍:shù,zhù,澎:pēng,péng,澛:lǔ,
澜:lán,澡:zǎo,cāo,澥:xiè,澧:lǐ,澪:líng,澭:yōng,yǒng,澳:ào,yù,澴:huán,xuàn,
澶:chán,dàn,zhān,澹:dàn,tán,dān,shàn,澼:pì,澽:jù,激:jī,jiào,jiāo,濂:lián,xiǎn,濉:suī,濋:chǔ,
濑:lài,濒:bīn,濞:bì,pì,濠:háo,濡:rú,ruǎn,ér,nuán,nuò,濩:huò,hù,濮:pú,濯:zhuó,shuò,zhào,
瀌:biāo,瀍:chán,瀑:pù,bào,bó,瀔:gǔ,瀚:hàn,瀛:yíng,瀣:xiè,瀱:jì,
瀵:fèn,瀹:yuè,yào,瀼:ráng,ràng,nǎng,灈:qú,灌:guàn,huàn,灏:hào,灞:bà,火:huǒ,huō,
灭:miè,灯:dēng,dīng,灰:huī,灵:líng,灶:zào,灸:jiǔ,灼:zhuó,灾:zāi,
灿:càn,炀:yáng,炅:jiǒng,guì,炆:wén,炉:lú,炊:chuī,炌:kài,炎:yán,yàn,tán,
炒:chǎo,炔:guì,quē,xuè,炕:kàng,hāng,炖:dùn,tún,炘:xīn,xìn,炙:zhì,炜:wěi,炝:qiàng,
炟:dá,炣:kě,炫:xuàn,炬:jù,炭:tàn,炮:pào,páo,bāo,炯:jiǒng,炱:tái,
炳:bǐng,炷:zhù,炸:zhà,zhá,点:diǎn,炻:shí,炼:liàn,炽:chì,烀:hū,
烁:shuò,烂:làn,烃:tīng,烈:liè,烊:yáng,yàng,烔:tóng,dòng,烘:hōng,烙:lào,luò,
烛:zhú,chóng,烜:xuǎn,xuān,huǐ,烝:zhēng,烟:yān,yīn,烠:huí,huǐ,ǎi,烤:kǎo,烦:fán,烧:shāo,
烨:yè,烩:huì,烫:tàng,烬:jìn,热:rè,烯:xī,烶:tǐng,烷:wán,
烹:pēng,烺:lǎng,烻:yàn,shān,烽:fēng,焆:juān,yè,yuè,yuān,焉:yān,yí,焊:hàn,焌:jùn,qū,
焐:wù,焓:hán,焕:huàn,焖:mèn,焗:jú,焘:dào,tāo,焙:bèi,焚:fén,fèn,
焜:kūn,焞:tūn,tuī,jùn,焦:jiāo,qiáo,焯:chāo,zhuō,zhuó,chuò,焰:yàn,焱:yàn,yì,然:rán,煁:chén,
煃:kuǐ,煅:duàn,煊:xuān,煋:xīng,煌:huáng,煎:jiān,jiàn,jiǎn,煓:tuān,煜:yù,
煞:shā,shà,煟:wèi,煤:méi,煦:xù,xiū,照:zhào,煨:wēi,yù,煮:zhǔ,煲:bāo,
煳:hú,煴:yūn,yùn,wěn,煸:biān,煺:tuì,煽:shān,熄:xī,熇:hè,xiāo,kǎo,kào,熊:xióng,
熏:xūn,xùn,熔:róng,熘:liū,熙:xī,yí,熛:biāo,熜:cōng,zǒng,熟:shú,shóu,熠:yì,
熥:tēng,tōng,熨:yùn,yù,wèi,熬:áo,āo,熵:shāng,熹:xī,熻:xī,燃:rán,燊:shēn,
燋:jiāo,qiáo,jué,zhuó,燎:liáo,liǎo,liào,燏:yù,燔:fán,fén,燕:yàn,yān,燚:yì,燠:yù,ào,燥:zào,sào,
燧:suì,燮:xiè,燹:xiǎn,bìng,爆:bào,bó,爇:ruò,爔:xī,爚:yuè,爝:jué,jiào,
爟:guàn,爨:cuàn,爪:zhǎo,zhuǎ,爬:pá,爰:yuán,爱:ài,爵:jué,父:fù,fǔ,
爷:yé,爸:bà,爹:diē,爻:yáo,xiào,爽:shuǎng,shuāng,爿:pán,qiáng,牁:kē,牂:zāng,
片:piàn,piān,pàn,版:bǎn,牌:pái,牍:dú,牒:dié,牖:yǒu,牙:yá,yà,牚:chēng,chèng,
牛:niú,牝:pìn,牟:móu,mù,mào,牡:mǔ,牢:láo,lào,lóu,牤:māng,牥:fāng,牦:máo,
牧:mù,物:wù,牮:jiàn,牯:gǔ,牲:shēng,牵:qiān,特:tè,牺:xī,
牻:máng,牾:wǔ,wú,牿:gù,犀:xī,犁:lí,犄:jī,yī,犇:bēn,犊:dú,
犋:jù,犍:jiān,qián,jiǎn,犏:piān,犒:kào,犟:jiàng,犨:chōu,犬:quǎn,犯:fàn,
犰:qiú,犴:àn,hān,án,jiàn,状:zhuàng,犷:guǎng,犸:mà,mǎ,犹:yóu,yòu,狁:yǔn,狂:kuáng,jué,
狃:niǔ,nǜ,狄:dí,tì,狈:bèi,狉:pī,狍:páo,狎:xiá,狐:hú,狒:fèi,
狗:gǒu,狙:jū,狝:xiǎn,狞:níng,狠:hěn,yán,kěn,hǎng,狡:jiǎo,xiào,狨:róng,狩:shòu,
独:dú,狭:xiá,狮:shī,狯:kuài,狰:zhēng,狱:yù,狲:sūn,狳:yú,
狴:bì,狷:juàn,狸:lí,狺:yín,狻:suān,xùn,jùn,狼:láng,lǎng,làng,hǎng,猁:lì,猃:xiǎn,
猄:jīng,猇:xiāo,猊:ní,猎:liè,xī,què,猕:mí,猖:chāng,猗:yī,yǐ,jì,ē,wēi,猛:měng,
猜:cāi,猝:cù,猞:shē,猡:luó,猢:hú,猥:wěi,wèi,猩:xīng,猪:zhū,
猫:māo,miáo,máo,猬:wèi,献:xiàn,猯:tuān,猰:yà,jiá,qiè,猱:náo,猴:hóu,猷:yóu,
猹:chá,猺:yáo,猾:huá,猿:yuán,獍:jìng,獐:zhāng,獒:áo,獗:jué,
獠:liáo,lǎo,獬:xiè,hǎ,jiě,獭:tǎ,獯:xūn,獴:měng,méng,獾:huān,quán,玃:jué,玄:xuán,xuàn,
率:lǜ,shuài,lüè,玉:yù,王:wáng,wàng,yù,玎:dīng,玑:jī,玒:hóng,玓:dì,玕:gān,
玖:jiǔ,玘:qǐ,玙:yú,玚:chàng,yáng,玛:mǎ,玞:fū,玟:wén,mín,玠:jiè,
玡:yá,yà,玢:bīn,fēn,玤:bàng,玥:yuè,玦:jué,玩:wán,玫:méi,玭:pín,
玮:wěi,环:huán,现:xiàn,玱:qiāng,玲:líng,玳:dài,玶:píng,玷:diàn,diān,
玹:xuán,xuàn,xián,玺:xǐ,玻:bō,玼:cǐ,cī,cuō,玿:sháo,珀:pò,珂:kē,珅:shēn,
珇:zǔ,jù,珈:jiā,珉:mín,珊:shān,珋:liǔ,珌:bì,珍:zhēn,珏:jué,
珐:fà,珑:lóng,珒:jīn,珕:lì,珖:guāng,珙:gǒng,珛:xiù,珝:xǔ,
珞:luò,lì,珠:zhū,珢:yín,kèn,珣:xún,珥:ěr,珦:xiàng,珧:yáo,珩:háng,héng,
珪:guī,珫:chōng,班:bān,珰:dāng,珲:huī,hún,珵:chéng,tǐng,珷:wǔ,wù,珸:wú,
珹:chéng,珺:jùn,珽:tǐng,琀:hán,球:qiú,琄:xuàn,琅:láng,làng,理:lǐ,
琇:xiù,琈:fú,fū,琉:liú,琊:yá,琎:jìn,jīn,琏:liǎn,琐:suǒ,琔:diàn,
琚:jū,琛:chēn,琟:wéi,yù,琡:chù,shū,琢:zuó,zhuó,琤:chēng,琥:hǔ,琦:qí,
琨:kūn,琪:qí,琫:běng,琬:wǎn,琭:lù,琮:cóng,琯:guǎn,gùn,guān,guàn,琰:yǎn,
琲:bèi,琳:lín,琴:qín,琵:pí,琶:pá,琼:qióng,瑀:yǔ,瑁:mào,
瑂:méi,瑃:chūn,瑄:xuān,瑅:tí,瑆:xīng,瑑:zhuàn,瑓:liàn,瑔:quán,
瑕:xiá,瑖:duàn,瑗:yuàn,huán,瑙:nǎo,瑚:hú,瑛:yīng,瑜:yú,瑝:huáng,
瑞:ruì,瑟:sè,瑢:róng,瑧:zhēn,瑨:jìn,瑬:liú,瑭:táng,瑰:guī,
瑱:zhèn,tiàn,瑳:cuō,瑶:yáo,瑷:ài,瑾:jǐn,jìn,璀:cuǐ,璁:cōng,璃:lí,
璆:qiú,璇:xuán,璈:áo,璋:zhāng,璎:yīng,璐:lù,璒:dēng,璘:lín,
璜:huáng,璞:pú,璟:jǐng,璠:fán,璥:jǐng,璧:bì,璨:càn,璩:qú,
璪:zǎo,璬:jiǎo,璮:tǎn,璱:sè,璲:suì,璺:wèn,瓀:ruǎn,瓒:zàn,
瓖:xiāng,瓘:guàn,瓜:guā,瓞:dié,瓠:hù,hú,huò,gū,瓢:piáo,瓣:bàn,瓤:ráng,
瓦:wǎ,wà,瓮:wèng,瓯:ōu,瓴:líng,瓶:píng,瓷:cí,瓻:chī,瓿:bù,pǒu,
甄:zhēn,zhèn,juàn,甍:méng,甏:bèng,甑:zèng,甓:pì,甗:yǎn,甘:gān,hān,甚:shèn,shén,
甜:tián,生:shēng,甡:shēn,甥:shēng,甦:sū,用:yòng,甩:shuǎi,甪:lù,
甫:fǔ,fū,pǔ,甬:yǒng,dòng,甭:béng,qì,甯:níng,nìng,田:tián,由:yóu,yāo,甲:jiǎ,申:shēn,
电:diàn,男:nán,甸:diān,diàn,tián,shèng,yìng,町:tīng,tǐng,dīng,zhèng,tiǎn,画:huà,甾:zāi,zī,畀:bì,畅:chàng,
畈:fàn,畋:tián,界:jiè,畎:quǎn,畏:wèi,wēi,wěi,畔:pàn,畖:wā,留:liú,liù,liǔ,
畚:běn,畛:zhěn,畜:chù,xù,畤:zhì,chóu,shì,略:lüè,畦:qí,番:fān,pān,fán,bō,pó,pán,pàn,pí,畬:shē,yú,
畯:jùn,畲:shē,畴:chóu,畸:jī,qí,畹:wǎn,yuǎn,畿:jī,疁:liú,疃:tuǎn,
疆:jiāng,jiàng,疍:dàn,疏:shū,疐:zhì,dì,疑:yí,níng,疔:dīng,nè,疖:jiē,疗:liáo,
疙:gē,yì,疚:jiù,疝:shàn,疟:nüè,yào,疠:lì,疡:yáng,疢:chèn,疣:yóu,yòu,
疤:bā,疥:jiè,疫:yì,疬:lì,疭:zòng,疮:chuāng,疯:fēng,疰:zhù,
疱:pào,疲:pí,疳:gān,疴:kē,ē,qià,疵:cī,zī,zhài,jì,疸:dǎn,da,疹:zhěn,chèn,疼:téng,
疽:jū,jǔ,疾:jí,痂:jiā,痃:xuán,痄:zhà,病:bìng,症:zhèng,zhēng,痈:yōng,
痉:jìng,痊:quán,痍:yí,痒:yǎng,yáng,痓:chì,zhì,痔:zhì,痕:hén,gèn,痘:dòu,
痛:tòng,痞:pǐ,痢:lì,痣:zhì,痤:cuó,痦:wù,pī,痧:shā,痨:láo,
痪:huàn,tuǎn,痫:xián,痰:tán,痱:fèi,féi,fěi,痴:chī,痹:bì,痼:gù,痿:wěi,
瘀:yū,瘁:cuì,瘃:zhú,瘅:dān,dàn,瘆:shèn,瘊:hóu,瘌:là,瘐:yǔ,yù,
瘕:jiǎ,xiā,瘗:yì,瘘:lòu,瘙:sào,瘛:chì,瘟:wēn,wò,yūn,瘠:jí,瘢:bān,
瘤:liú,瘥:chài,cuó,瘦:shòu,瘩:dā,dá,da,瘪:biě,biē,瘫:tān,瘭:biāo,瘰:luǒ,
瘳:chōu,lù,瘴:zhàng,瘵:zhài,jì,瘸:qué,瘼:mò,瘾:yǐn,瘿:yǐng,癀:huáng,
癃:lóng,癌:ái,yán,癍:bān,癔:yì,癖:pǐ,癗:lěi,癜:diàn,癞:lài,
癣:xuǎn,癫:diān,癯:qú,癸:guǐ,登:dēng,dé,白:bái,bó,百:bǎi,bó,mò,癿:qié,bié,
皂:zào,的:de,dī,dí,dì,皆:jiē,皇:huáng,wǎng,皈:guī,皋:gāo,háo,gū,皎:jiǎo,皑:ái,
皓:hào,huī,皕:bì,皖:wǎn,huàn,皙:xī,皛:xiǎo,jiǎo,pò,皞:hào,皤:pó,pán,皦:jiǎo,
皭:jiào,皮:pí,皱:zhòu,皲:jūn,皴:cūn,皿:mǐn,mǐng,盂:yú,盅:zhōng,chōng,
盆:pén,盈:yíng,盉:hé,益:yì,盍:hé,kě,盎:àng,盏:zhǎn,盐:yán,
监:jiān,jiàn,盒:hé,ān,盔:kuī,盖:gài,gě,盗:dào,盘:pán,盛:shèng,chéng,盟:méng,mèng,míng,
盥:guàn,盦:ān,目:mù,盯:dīng,chéng,盱:xū,盲:máng,直:zhí,盷:tián,xián,mín,
相:xiāng,xiàng,盹:dǔn,zhūn,盼:pàn,fén,盾:dùn,shǔn,yǔn,省:shěng,xǐng,xiǎn,眄:miǎn,miàn,眇:miǎo,miào,眈:dān,chěn,
眉:méi,眊:mào,mèi,看:kàn,kān,眍:kōu,眙:yí,chì,眚:shěng,真:zhēn,眠:mián,miǎn,mǐn,
眢:yuān,眦:zì,眨:zhǎ,眩:xuàn,huàn,juàn,眬:lóng,眭:suī,guì,huī,xié,wèi,眯:mī,mí,mǐ,mì,眵:chī,
眶:kuàng,眷:juàn,眸:móu,眺:tiào,眼:yǎn,wěn,着:zhe,zhāo,zháo,zhuó,睁:zhēng,睃:suō,jùn,juān,
睄:shào,qiáo,xiāo,睇:dì,tī,tí,睎:xī,睐:lài,睑:jiǎn,睚:yá,睛:jīng,jǐng,睡:shuì,
睢:suī,huī,wěi,督:dū,睥:pì,bì,睦:mù,睨:nì,睫:jié,shè,睬:cǎi,睹:dǔ,
睽:kuí,kuì,jì,睾:gāo,hào,睿:ruì,瞀:mào,wú,瞄:miáo,瞅:chǒu,瞋:chēn,tián,tiàn,shèn,瞌:kē,
瞍:sǒu,瞎:xiā,瞑:míng,méng,mián,瞒:mán,瞟:piǎo,piào,piāo,瞠:chēng,zhèng,瞡:guī,瞢:méng,máng,mèng,
瞥:piē,bì,瞧:qiáo,瞩:zhǔ,瞪:dèng,瞫:shěn,瞬:shùn,瞭:liǎo,liào,瞰:kàn,
瞳:tóng,瞵:lín,lìn,lián,瞻:zhān,瞽:gǔ,瞿:qú,jù,jí,矍:jué,矗:chù,矛:máo,
矜:jīn,qín,guān,矞:yù,jué,xù,矢:shǐ,矣:yǐ,xián,知:zhī,zhì,矧:shěn,矩:jǔ,矫:jiǎo,jiáo,
矬:cuó,短:duǎn,矮:ǎi,矰:zēng,石:shí,dàn,矶:jī,矸:gān,gàn,gǎn,hàn,矻:kū,qià,
矼:gāng,kòng,qiāng,矾:fán,矿:kuàng,砀:dàng,码:mǎ,砂:shā,砄:jué,砆:fū,
砉:huò,xū,huā,砌:qì,qiè,砍:kǎn,砑:yà,砒:pī,研:yán,yàn,xíng,砖:zhuān,砗:chē,
砘:dùn,砚:yàn,砜:fēng,砝:fá,fǎ,jié,gé,砟:zhǎ,zhà,zuó,砠:jū,zū,砣:tuó,砥:dǐ,zhǐ,
砧:zhēn,砫:zhù,zhǔ,砬:lá,lì,lā,砭:biān,砮:nǔ,nú,砰:pēng,pīng,pèng,破:pò,砵:bō,è,
砷:shēn,砸:zá,砹:ài,砺:lì,砻:lóng,砼:tóng,砾:lì,础:chǔ,
硁:kēng,硅:guī,hè,硇:náo,硊:wěi,huì,guì,硌:gè,luò,lì,硍:xiàn,kèn,yín,kēng,yǐn,硎:xíng,kēng,硐:dòng,tóng,liú,
硒:xī,硔:hóng,gǒng,硕:shuò,硖:xiá,硗:qiāo,硙:wéi,wèi,硚:qiáo,硝:xiāo,qiào,
硪:wò,é,yǐ,硫:liú,chù,硬:yìng,gěng,硭:máng,确:què,硼:péng,pēng,硿:kōng,kòng,碃:qìng,
碇:dìng,碈:mín,hūn,碉:diāo,碌:lù,liù,luò,碍:ài,碎:suì,碏:què,xī,碑:bēi,
碓:duì,duī,碗:wǎn,碘:diǎn,碚:bèi,碛:qì,碜:chěn,碟:dié,shé,碡:dú,zhóu,
碣:jié,kě,yà,碥:biǎn,碧:bì,碨:wèi,wěi,碰:pèng,碱:jiǎn,xián,碲:dì,碳:tàn,
碴:chá,chā,碶:qì,碹:xuàn,碾:niǎn,磁:cí,磅:bàng,páng,pāng,磉:sǎng,磊:lěi,
磋:cuō,磏:lián,qiān,磐:pán,磔:zhé,磕:kē,kě,磙:gǔn,磜:qì,qī,磡:kàn,
磨:mó,mò,磬:qìng,qǐng,磲:qú,磴:dèng,dēng,磷:lín,lìn,lǐn,líng,磹:tán,diàn,磻:pán,bō,礁:jiāo,
礅:dūn,礌:léi,lèi,lěi,礓:jiāng,礞:méng,礴:bó,礵:shuāng,示:shì,qí,zhì,shí,礼:lǐ,
社:shè,祀:sì,祁:qí,zhǐ,祃:mà,祆:xiān,祇:qí,zhǐ,chí,zhī,祈:qí,guǐ,祉:zhǐ,
祊:bēng,fāng,祋:duì,祎:yī,祏:shí,祐:yòu,祓:fú,fèi,祕:mì,bì,祖:zǔ,jiē,
祗:zhī,祚:zuò,祛:qū,祜:hù,祝:zhù,zhòu,chù,神:shén,shēn,祟:suì,祠:cí,sì,
祢:mí,nǐ,祥:xiáng,祧:tiāo,票:piào,piāo,祭:jì,zhài,祯:zhēn,祲:jìn,祷:dǎo,
祸:huò,祺:qí,祼:guàn,祾:líng,禀:bǐng,禁:jìn,jīn,禄:lù,禅:chán,shàn,
禊:xì,禋:yīn,福:fú,fù,禒:xiǎn,禔:zhī,tí,禘:dì,禚:zhuó,禛:zhēn,
禤:xuān,禧:xǐ,xī,禳:ráng,禹:yǔ,禺:yú,yù,离:lí,chī,禽:qín,禾:hé,
秀:xiù,私:sī,秃:tū,秆:gǎn,秉:bǐng,秋:qiū,种:zhǒng,chóng,zhòng,科:kē,kè,
秒:miǎo,秕:bǐ,秘:mì,bì,bié,租:zū,jū,秣:mò,秤:chèng,chēng,píng,秦:qín,秧:yāng,
秩:zhì,秫:shú,秬:jù,秭:zǐ,积:jī,zhǐ,称:chēng,chèn,chèng,秸:jiē,jí,移:yí,chǐ,yì,
秽:huì,秾:nóng,稀:xī,稂:láng,稃:fū,稆:lǚ,程:chéng,稌:tú,shǔ,
稍:shāo,shào,税:shuì,tuō,tuì,tuàn,稑:lù,稔:rěn,稗:bài,稙:zhī,zhì,稚:zhì,稞:kē,huà,
稠:chóu,tiáo,diào,稣:sū,稳:wěn,稷:jì,zè,稹:zhěn,zhēn,biān,稻:dào,稼:jià,稽:jī,qǐ,
稿:gǎo,穄:jì,穆:mù,穑:sè,穗:suì,穙:pú,穜:zhǒng,tóng,zhòng,穟:suì,
穰:ráng,rǎng,réng,穴:xué,jué,究:jiū,jiù,穷:qióng,穸:xī,穹:qióng,qiōng,kōng,空:kōng,kòng,kǒng,穿:chuān,chuàn,yuān,
窀:zhūn,tún,突:tū,窃:qiè,窄:zhǎi,窅:yǎo,窈:yǎo,yào,窊:wā,窍:qiào,
窎:diào,窑:yáo,窒:zhì,dié,窕:tiǎo,tiāo,窖:jiào,zào,窗:chuāng,cōng,窘:jiǒng,窜:cuàn,
窝:wō,窟:kū,窠:kē,窣:sū,窥:kuī,窦:dòu,窨:xūn,yìn,yīn,窬:yú,dōu,
窭:jù,窳:yǔ,yú,窸:xī,窿:lóng,立:lì,wèi,竑:hóng,竖:shù,竘:qǔ,kǒu,
站:zhàn,zhān,竞:jìng,竟:jìng,章:zhāng,zhàng,竣:jùn,童:tóng,zhōng,竦:sǒng,竫:jìng,
竭:jié,端:duān,竹:zhú,竺:zhú,dǔ,竽:yú,竿:gān,gàn,gǎn,笃:dǔ,笄:jī,
笆:bā,笈:jí,笊:zhào,笋:sǔn,笏:hù,wěn,wù,笑:xiào,笔:bǐ,笕:jiǎn,
笙:shēng,笛:dí,笞:chī,笠:lì,笤:tiáo,shào,笥:sì,符:fú,笨:bèn,
笪:dá,笫:zǐ,第:dì,笮:zé,zuó,zhà,笯:nú,笱:gǒu,笳:jiā,笸:pǒ,
笺:jiān,笼:lóng,lǒng,笾:biān,筀:guì,筅:xiǎn,筇:qióng,等:děng,筋:jīn,qián,
筌:quán,筏:fá,筐:kuāng,筑:zhù,zhú,筒:tǒng,dòng,tóng,答:dá,dā,策:cè,筘:kòu,
筚:bì,筛:shāi,筜:dāng,筝:zhēng,筠:yún,jūn,筢:pá,筤:láng,làng,筥:jǔ,
筦:guǎn,筮:shì,筱:xiǎo,筲:shāo,筵:yán,筶:gào,gǎo,筷:kuài,筹:chóu,
筻:gàng,筼:yún,签:qiān,简:jiǎn,箅:bì,箍:gū,箐:qìng,jīng,qiāng,箓:lù,
箔:bó,箕:jī,箖:lín,lǐn,算:suàn,箜:kōng,管:guǎn,箢:yuān,wǎn,箦:zé,
箧:qiè,箨:tuò,箩:luó,箪:dān,箫:xiāo,箬:ruò,nà,箭:jiàn,箱:xiāng,
箴:zhēn,jiǎn,箸:zhù,zhuó,篁:huáng,篆:zhuàn,篇:piān,築:zhù,zhú,篌:hóu,篑:kuì,
篓:lǒu,篙:gāo,篚:fěi,篝:gōu,篡:cuàn,篥:lì,篦:bì,pí,篪:chí,
篮:lán,篯:jiān,jiǎn,篱:lí,篷:péng,篼:dōu,篾:miè,簃:yí,簇:cù,chuò,còu,
簉:zào,chòu,簋:guǐ,簌:sù,簏:lù,簕:lè,簖:duàn,簝:liáo,簟:diàn,
簠:fǔ,簧:huáng,簪:zān,zǎn,簰:pái,簸:bǒ,bò,簿:bù,bó,籀:zhòu,籁:lài,
籍:jí,jiè,籥:yuè,米:mǐ,籴:dí,zá,类:lèi,籼:xiān,籽:zǐ,粉:fěn,
粑:bā,粒:lì,粕:pò,粗:cū,粘:zhān,nián,粜:tiào,粝:lì,粞:xī,
粟:sù,粢:zī,cí,jì,粤:yuè,粥:zhōu,yù,粪:fèn,粮:liáng,粱:liáng,粲:càn,
粳:jīng,粹:cuì,suì,粼:lín,lǐn,粽:zòng,精:jīng,qíng,jìng,粿:guǒ,huà,糁:sǎn,shēn,糅:róu,
糇:hóu,糈:xǔ,糊:hú,hū,hù,糌:zān,糍:cí,糒:bèi,糕:gāo,糖:táng,
糗:qiǔ,糙:cāo,糜:mí,méi,糟:zāo,糠:kāng,糨:jiàng,jiāng,糯:nuò,糵:niè,
系:xì,jì,紊:wěn,wèn,素:sù,索:suǒ,紧:jǐn,紫:zǐ,累:lèi,léi,lěi,lǜ,liè,絜:jié,xié,qià,jiá,qì,
絮:xù,chù,nǜ,nà,絷:zhí,綦:qí,qì,綮:qǐ,qìng,qǐng,縠:hú,縢:téng,縻:mí,繁:fán,pó,pán,
繄:yī,yì,繇:yáo,yóu,zhòu,纂:zuǎn,纛:dào,dú,纠:jiū,纡:yū,红:hóng,gōng,纣:zhòu,
纤:xiān,qiàn,纥:gē,hé,约:yuē,yāo,级:jí,纨:wán,纩:kuàng,纪:jì,jǐ,纫:rèn,
纬:wěi,纭:yún,纮:hóng,纯:chún,纰:pī,纱:shā,纲:gāng,纳:nà,
纴:rèn,纵:zòng,纶:lún,guān,纷:fēn,纸:zhǐ,纹:wén,wèn,纺:fǎng,纻:zhù,
纼:zhèn,纽:niǔ,纾:shū,线:xiàn,绀:gàn,绁:xiè,绂:fú,练:liàn,
组:zǔ,绅:shēn,细:xì,织:zhī,终:zhōng,绉:zhòu,绊:bàn,绋:fú,
绌:chù,绍:shào,绎:yì,经:jīng,jìng,绐:dài,绑:bǎng,绒:róng,结:jié,jiē,
绔:kù,绕:rào,rǎo,绖:dié,绗:háng,绘:huì,给:gěi,jǐ,绚:xuàn,绛:jiàng,
络:luò,lào,绝:jué,绞:jiǎo,统:tǒng,绠:gěng,绡:xiāo,绢:juàn,绣:xiù,
绤:xì,绥:suí,绦:tāo,继:jì,绨:tí,tì,绩:jì,jī,绪:xù,绫:líng,
续:xù,绮:qǐ,绯:fēi,绰:chuò,chāo,绱:shàng,绲:gǔn,绳:shéng,维:wéi,
绵:mián,绶:shòu,绷:bēng,běng,bèng,绸:chóu,绹:táo,绺:liǔ,绻:quǎn,综:zōng,zèng,
绽:zhàn,绾:wǎn,绿:lǜ,lù,缀:zhuì,缁:zī,缂:kè,缃:xiāng,缄:jiān,
缅:miǎn,缆:lǎn,缇:tí,缈:miǎo,缉:jī,qī,缊:yūn,yùn,缌:sī,缎:duàn,
缐:xiàn,缑:gōu,缒:zhuì,缓:huǎn,缔:dì,缕:lǚ,编:biān,缗:mín,
缘:yuán,缙:jìn,缚:fù,缛:rù,缜:zhěn,缝:fèng,féng,缞:cuī,缟:gǎo,
缠:chán,缡:lí,缢:yì,缣:jiān,缤:bīn,缥:piāo,piǎo,缦:màn,缧:léi,
缨:yīng,缩:suō,sù,缪:móu,miào,miù,缫:sāo,缬:xié,缭:liáo,缮:shàn,缯:zēng,zèng,
缰:jiāng,缱:qiǎn,缲:qiāo,sāo,缳:huán,缴:jiǎo,zhuó,缵:zuǎn,缶:fǒu,缸:gāng,
缺:quē,kuǐ,罂:yīng,罄:qìng,罅:xià,罍:léi,罐:guàn,网:wǎng,罔:wǎng,wáng,
罕:hǎn,hàn,罗:luó,luō,罘:fú,罚:fá,罟:gǔ,罡:gāng,罢:bà,ba,罨:yǎn,
罩:zhào,罪:zuì,置:zhì,罱:lǎn,nǎn,署:shǔ,罴:pí,罶:liǔ,罹:lí,
罽:jì,罾:zēng,羁:jī,羊:yáng,羌:qiāng,美:měi,羑:yǒu,羓:bā,
羔:gāo,羕:yàng,羖:gǔ,羚:líng,羝:dī,羞:xiū,羟:qiǎng,羡:xiàn,yán,yí,
群:qún,羧:suō,zuī,羯:jié,羰:tāng,羱:yuán,羲:xī,羸:léi,lián,羹:gēng,láng,
羼:chàn,羽:yǔ,hù,羿:yì,翀:chōng,翁:wēng,wěng,翂:fēn,翃:hóng,翅:chì,
翈:xiá,翊:yì,翌:yì,翎:líng,翔:xiáng,翕:xī,翘:qiào,qiáo,翙:huì,
翚:huī,翛:xiāo,shū,翟:dí,zhái,翠:cuì,翡:fěi,翥:zhù,翦:jiǎn,翩:piān,
翮:hé,lì,翯:hè,hào,翰:hàn,翱:áo,翳:yì,翷:lín,翻:fān,翼:yì,
翾:xuān,耀:yào,老:lǎo,考:kǎo,耄:mào,者:zhě,耆:qí,zhǐ,shì,耇:gǒu,
耋:dié,而:ér,néng,耍:shuǎ,耏:nài,ér,耐:nài,néng,耑:duān,zhuān,耒:lěi,耔:zǐ,
耕:gēng,耖:chào,耗:hào,máo,mào,耘:yún,耙:bà,pá,耜:sì,耠:huō,耢:lào,
耤:jí,jiè,耥:tāng,tǎng,耦:ǒu,耧:lóu,耨:nòu,耩:jiǎng,耪:pǎng,耰:yōu,
耱:mò,耳:ěr,réng,耵:dīng,耶:yé,yē,xié,耷:dā,zhé,耸:sǒng,耻:chǐ,耽:dān,
耿:gěng,聂:niè,聃:dān,聆:líng,聊:liáo,liú,聋:lóng,职:zhí,聍:níng,
聒:guā,guō,联:lián,聘:pìn,pìng,聚:jù,聩:kuì,聪:cōng,聱:áo,yóu,聿:yù,
肃:sù,肄:yì,sì,肆:sì,tì,肇:zhào,肉:ròu,rù,肋:lē,lèi,lè,jīn,肌:jī,jì,肓:huāng,
肖:xiào,xiāo,肘:zhǒu,肚:dù,dǔ,肛:gāng,肝:gān,肟:wò,肠:cháng,股:gǔ,
肢:zhī,shì,肤:fū,肥:féi,bǐ,肩:jiān,xián,肪:fáng,肫:zhūn,chún,tún,zhuō,肭:nà,nù,肮:āng,háng,gāng,
肯:kěn,肱:gōng,育:yù,zhòu,yō,肴:yáo,肷:qiǎn,xù,肸:xī,bì,肺:fèi,pèi,肼:jǐng,
肽:tài,肾:shèn,肿:zhǒng,胀:zhàng,胁:xié,胂:shèn,shēn,chēn,胃:wèi,胄:zhòu,
胆:dǎn,tán,tǎn,dá,胈:bá,背:bèi,bēi,胍:guā,gū,hù,胎:tāi,胖:pàng,pán,pàn,胗:zhēn,zhěn,zhūn,胙:zuò,
胚:pēi,胛:jiǎ,胜:shèng,xīng,qìng,shēng,胝:zhī,chī,dì,胞:bāo,páo,pào,胠:qū,胡:hú,胣:chǐ,
胤:yìn,胥:xū,xǔ,胧:lóng,胨:dòng,胩:kǎ,胪:lú,胫:jìng,胬:nǔ,nǚ,
胭:yān,胯:kuà,kuǎ,胰:yí,胱:guāng,胲:hǎi,gāi,gǎi,胳:gē,gé,gā,胴:dòng,胶:jiāo,xiáo,
胸:xiōng,胺:àn,è,胼:pián,能:néng,tái,nái,nài,xióng,脂:zhī,zhǐ,脆:cuì,脉:mài,mò,脊:jí,jǐ,
脍:kuài,脎:sà,脏:zàng,zāng,脐:qí,脑:nǎo,脒:mǐ,脓:nóng,脔:luán,jī,
脖:bó,bō,脘:wǎn,huàn,脚:jiǎo,jué,脞:cuǒ,qiē,脟:liè,luán,pāo,脩:xiū,yǒu,tiáo,xiāo,脬:pāo,脯:pú,fǔ,
脱:tuō,tuì,脲:niào,脶:luó,脸:liǎn,脾:pí,pái,bì,pì,脿:biāo,biǎo,biào,腆:tiǎn,腈:jīng,
腊:là,xī,腋:yè,腌:yān,ā,āng,腐:fǔ,腑:fǔ,腒:jū,腓:féi,腔:qiāng,kòng,
腕:wàn,腘:guó,腙:zōng,腚:dìng,腜:méi,腠:còu,腥:xīng,腧:shù,yú,
腨:shuàn,腩:nǎn,腬:róu,腭:è,腮:sāi,腯:tú,dùn,腰:yāo,腱:jiàn,qián,
腴:yú,腹:fù,腺:xiàn,腻:nì,腼:miǎn,腽:wà,腾:téng,腿:tuǐ,
膀:bǎng,pāng,páng,bàng,pǎng,膂:lǚ,膈:gé,膊:bó,pò,liè,膏:gāo,gào,膑:bìn,膒:ōu,膘:biāo,piǎo,
膙:jiǎng,膛:táng,tāng,膜:mó,膝:xī,膦:lìn,liǎn,膨:péng,pèng,膳:shàn,膺:yīng,
膻:shān,dàn,臀:tún,臂:bì,bei,臃:yōng,臆:yì,yǐ,臊:sāo,sào,臌:gǔ,臑:nào,rú,ér,nèn,nuǎn,
臜:zā,臣:chén,臧:zāng,cáng,zàng,自:zì,臬:niè,臭:chòu,xiù,至:zhì,dié,致:zhì,zhuì,
臻:zhēn,臼:jiù,臾:yú,yǔ,yǒng,kuì,舀:yǎo,舁:yú,舂:chōng,chuāng,zhōng,舄:xì,què,tuō,舅:jiù,
舆:yú,舌:shé,guā,舍:shě,shè,shì,舐:shì,舒:shū,yù,舔:tiǎn,tān,舛:chuǎn,舜:shùn,
舞:wǔ,舟:zhōu,舠:dāo,舢:shān,舣:yǐ,舥:pā,航:háng,舫:fǎng,
般:bān,pán,bǎn,bō,舭:bǐ,舯:zhōng,舰:jiàn,舱:cāng,舲:líng,舳:zhú,zhǒu,舴:zé,
舵:duò,舶:bó,舷:xián,舸:gě,船:chuán,舻:lú,舾:xī,艄:shāo,shào,
艅:yú,艇:tǐng,艉:wěi,艋:měng,艎:huáng,艏:shǒu,艘:sōu,艚:cáo,
艟:chōng,zhuàng,tóng,艨:méng,艮:gěn,gèn,hén,良:liáng,liǎng,艰:jiān,色:sè,shǎi,艳:yàn,艴:fú,bó,pèi,
艺:yì,艽:jiāo,qiú,艾:ài,yì,艿:nǎi,réng,rèng,节:jié,jiē,芃:péng,芄:wán,芈:mǐ,
芊:qiān,qiàn,芋:yù,yú,xū,yǔ,芍:sháo,xiào,què,dì,芎:qiōng,xiōng,芏:dù,芑:qǐ,芒:máng,huāng,huǎng,wáng,芗:xiāng,
芘:pí,bì,bǐ,芙:fú,芜:wú,芝:zhī,芟:shān,wěi,芠:wén,芡:qiàn,芣:fú,fǒu,fū,
芤:kōu,芥:jiè,gài,芦:lú,lǔ,hù,芨:jī,芩:qín,yín,芪:qí,chí,芫:yán,yuán,芬:fēn,
芭:bā,pā,芮:ruì,ruò,芯:xīn,xìn,芰:jì,花:huā,芳:fāng,芴:wù,hū,芷:zhǐ,
芸:yún,yùn,芹:qín,芼:mào,máo,芽:yá,芾:fèi,fú,苁:cōng,苄:biàn,苇:wěi,
苈:lì,苉:pǐ,苊:è,苋:xiàn,苌:cháng,苍:cāng,苎:zhù,苏:sū,
苑:yuàn,yuān,yù,yùn,苒:rǎn,苓:líng,lián,苔:tái,tāi,苕:sháo,tiáo,苗:miáo,苘:qǐng,苛:kē,hē,
苜:mù,苞:bāo,páo,biāo,苟:gǒu,gōu,苠:mín,苡:yǐ,苣:jù,qǔ,苤:piě,pī,若:ruò,ré,rè,rě,
苦:kǔ,gǔ,hù,苧:níng,zhù,苫:shān,shàn,tiān,chān,苯:běn,英:yīng,yāng,苴:jū,chá,zhǎ,zū,jiē,bāo,xié,苷:gān,苹:píng,pēng,
苻:fú,pú,苾:bì,bié,mì,茀:fú,bó,fèi,bèi,bì,茁:zhuó,zhú,茂:mào,范:fàn,茄:jiā,qié,茅:máo,
茆:máo,mǎo,茈:cí,zǐ,cǐ,chái,茉:mò,茋:zhǐ,dǐ,茌:chí,茎:jīng,茏:lóng,茑:niǎo,
茓:xué,茔:yíng,茕:qióng,茗:míng,茚:yìn,茛:gèn,jiàn,茜:qiàn,xī,茝:chǎi,zhǐ,
茧:jiǎn,chóng,茨:cí,茫:máng,huǎng,茬:chá,chí,茭:jiāo,xiào,qiào,茯:fú,茱:zhū,茳:jiāng,
茴:huí,茵:yīn,茶:chá,茸:rōng,róng,rǒng,茹:rú,茺:chōng,茼:tóng,茽:zhòng,
荀:xún,荁:huán,荃:quán,chuò,荄:gāi,荆:jīng,荇:xìng,草:cǎo,zào,荏:rěn,
荐:jiàn,荑:tí,yí,荒:huāng,huǎng,kāng,huáng,荓:píng,pēng,荔:lì,荖:lǎo,chā,荙:dá,荚:jiá,
荛:ráo,荜:bì,荞:qiáo,荟:huì,荠:jì,qí,荡:dàng,荣:róng,荤:hūn,xūn,
荥:xíng,yíng,荦:luò,荧:yíng,荨:xún,qián,荩:jìn,荪:sūn,荫:yīn,yìn,荬:mǎi,
荭:hóng,荮:zhòu,药:yào,荷:hé,hè,hē,荸:bí,荻:dí,荼:tú,chá,yé,shū,荽:suī,wěi,
莅:lì,莆:pú,fǔ,莉:lì,lí,chí,莎:shā,suō,suī,莒:jǔ,莓:méi,莘:shēn,xīn,莙:jūn,
莛:tíng,tǐng,莜:yóu,diào,dí,莝:cuò,莞:guǎn,guān,wǎn,莠:yǒu,xiù,莨:làng,liáng,láng,莩:fú,piǎo,莪:é,
莫:mò,mù,莰:kǎn,莱:lái,莲:lián,莳:shí,shì,莴:wō,莶:xiān,获:huò,
莸:yóu,莹:yíng,莺:yīng,莼:chún,莽:mǎng,máng,莿:cì,菀:wǎn,yù,yùn,菁:jīng,
菂:dì,菅:jiān,guān,菇:gū,菉:lù,lǜ,菊:jú,菌:jūn,jùn,菍:niè,rěn,菏:hé,gē,
菔:fú,菖:chāng,菘:sōng,菜:cài,菝:bá,菟:tú,tù,菠:bō,菡:hàn,
菥:xī,sī,菩:pú,bèi,bó,菪:dàng,菰:gū,菱:líng,菲:fēi,fěi,fèi,菹:jū,zū,jù,菼:tǎn,
菽:shū,jiāo,萁:qí,jī,萃:cuì,萄:táo,萆:bì,pì,bēi,bá,萋:qī,萌:méng,míng,萍:píng,
萎:wēi,wěi,wèi,萏:dàn,萑:huán,zhuī,萘:nài,萚:tuò,萜:tiē,萝:luó,萣:dìng,
萤:yíng,营:yíng,萦:yíng,萧:xiāo,萨:sà,萩:qiū,jiāo,萱:xuān,萳:nǎn,nán,
萸:yú,萹:biǎn,biān,pián,萼:è,落:luò,là,lào,luō,葆:bǎo,bāo,葎:lǜ,葑:fēng,fèng,葖:tū,
著:zhù,zhe,zhuó,chú,zhāo,zháo,葙:xiāng,葚:rèn,shèn,葛:gé,gě,葜:qiā,葡:pú,bèi,董:dǒng,zhǒng,葩:pā,
葫:hú,葬:zàng,葭:jiā,xiá,葰:suī,jùn,suǒ,葱:cōng,chuāng,葳:wēi,葴:zhēn,qián,葵:kuí,
葶:tíng,dǐng,葸:xǐ,葺:qì,蒂:dì,蒄:guān,蒇:chǎn,蒈:kǎi,蒉:kuì,
蒋:jiǎng,蒌:lóu,蒎:pài,蒐:sōu,huì,蒑:yīn,蒗:làng,蒙:méng,mēng,měng,蒜:suàn,
蒟:jǔ,蒡:bàng,páng,蒨:qiàn,蒯:kuǎi,kuài,蒱:pú,蒲:pú,bó,蒴:shuò,蒸:zhēng,
蒹:jiān,蒺:jí,蒻:ruò,蒽:ēn,蒿:hāo,gǎo,蓁:zhēn,qín,蓂:míng,mì,蓄:xù,
蓇:gǔ,gū,蓉:róng,蓊:wěng,蓍:shī,蓏:luǒ,蓐:rù,蓑:suō,suī,蓓:bèi,
蓖:bì,蓝:lán,la,蓟:jì,蓠:lí,蓢:lǎng,蓣:yù,蓥:yíng,蓦:mò,
蓬:péng,pèng,蓰:xǐ,蓼:liǎo,lù,lǎo,liǔ,蓿:xu,xù,sù,蔀:bù,蔃:qiáng,jiàng,蔈:biāo,piào,piǎo,biào,蔊:hǎn,hàn,
蔌:sù,蔑:miè,蔓:màn,mán,wàn,蔗:zhè,蔚:wèi,yù,蔟:cù,còu,chuò,蔡:cài,sà,cā,蔫:niān,yān,yàn,
蔬:shū,shǔ,蔷:qiáng,蔸:dōu,蔹:liǎn,蔺:lìn,蔻:kòu,蔼:ǎi,蔽:bì,biē,piē,
蕃:fān,bō,fán,pí,蕈:xùn,tán,蕉:jiāo,qiáo,qiāo,蕊:ruǐ,juǎn,蕖:qú,蕗:lù,蕙:huì,蕞:zuì,jué,zhuó,
蕤:ruí,蕨:jué,蕰:wēn,yùn,蕲:qí,蕴:yùn,蕹:wèng,yōng,蕺:jí,qiè,蕻:hóng,hòng,
蕾:lěi,薁:yù,ào,薄:báo,bó,bò,bù,薅:hāo,薇:wēi,薏:yì,薛:xuē,薜:bì,bò,bó,bài,pì,
薢:xiè,薤:xiè,薨:hōng,薪:xīn,薮:sǒu,薯:shǔ,薰:xūn,薳:wěi,yuǎn,
薷:rú,薸:piáo,薹:tái,薿:nǐ,藁:gǎo,藉:jí,jiè,藏:cáng,zàng,zāng,藐:miǎo,mò,
藓:xiǎn,藕:ǒu,藜:lí,藟:lěi,藠:jiào,藤:téng,藦:mò,mó,藨:biāo,pāo,
藩:fān,fán,藻:zǎo,藿:huò,hé,蘅:héng,蘑:mó,蘖:niè,bò,蘘:ráng,xiāng,nāng,蘧:qú,jù,
蘩:fán,蘸:zhàn,蘼:mí,虎:hǔ,hù,虏:lǔ,虐:nüè,虑:lǜ,bì,虒:sī,xī,tí,zhì,
虓:xiāo,虔:qián,虚:xū,虞:yú,虢:guó,虤:yán,虫:chóng,huǐ,虬:qiú,
虮:jǐ,jī,虱:shī,虷:hán,gān,虸:zǐ,虹:hóng,jiàng,hòng,gòng,虺:huī,huǐ,虻:méng,虼:gè,
虽:suī,虾:xiā,há,虿:chài,蚀:shí,蚁:yǐ,蚂:mǎ,mà,mā,蚄:fāng,bàng,蚆:bā,
蚊:wén,蚋:ruì,蚌:bàng,bèng,pí,fēng,蚍:pí,蚓:yǐn,蚕:cán,tiǎn,蚜:yá,蚝:háo,cì,
蚣:gōng,zhōng,蚤:zǎo,zhǎo,蚧:jiè,蚨:fú,蚩:chī,蚪:dǒu,蚬:xiǎn,蚯:qiū,
蚰:yóu,zhú,蚱:zhà,蚲:píng,蚴:yòu,yǒu,niù,蚶:hān,hán,蚺:rán,tiàn,蛀:zhù,蛃:bǐng,bīng,
蛄:gū,gǔ,蛆:qū,jū,蛇:shé,yí,tuó,chí,蛉:líng,蛊:gǔ,蛋:dàn,蛎:lì,蛏:chēng,
蛐:qū,蛑:móu,máo,蛔:huí,蛘:yáng,yǎng,蛙:wā,jué,蛛:zhū,蛞:kuò,shé,蛟:jiāo,
蛤:há,gé,hā,é,蛩:qióng,gǒng,蛭:zhì,蛮:mán,蛰:zhé,蛱:jiá,蛲:náo,蛳:sī,
蛴:qí,蛸:shāo,xiāo,蛹:yǒng,蛾:é,yǐ,蜀:shǔ,蜂:fēng,蜃:shèn,蜇:zhē,zhé,
蜈:wú,蜉:fú,蜊:lí,lì,蜍:chú,yú,蜎:yuān,xuān,蜐:jié,蜒:yán,yàn,dàn,蜓:tíng,diàn,
蜕:tuì,yuè,蜗:wō,蜘:zhī,蜚:fēi,fěi,pèi,bèi,蜜:mì,蜞:qí,蜡:là,qù,zhà,jí,蜢:měng,mèng,
蜣:qiāng,蜥:xī,蜩:tiáo,diào,蜮:yù,guō,蜱:pí,miáo,蜴:yì,xí,蜷:quán,juǎn,蜻:qīng,jīng,
蜾:guǒ,luǒ,蜿:wān,wǎn,蝇:yíng,蝈:guō,蝉:chán,蝌:kē,蝎:xiē,hé,蝓:yú,
蝗:huáng,蝘:yǎn,蝙:biān,pián,蝠:fú,蝣:yóu,蝤:qiú,yóu,jiū,蝥:máo,wú,wù,蝮:fù,
蝰:kuí,蝲:là,蝴:hú,蝶:dié,tiē,蝻:nǎn,蝼:lóu,蝽:chūn,蝾:róng,
螂:láng,螃:páng,bǎng,螅:xī,cì,螈:yuán,螋:sōu,融:róng,螗:táng,螟:míng,
螠:yì,螣:tè,téng,螨:mǎn,螫:shì,zhē,螬:cáo,螭:chī,螯:áo,螱:wèi,
螳:táng,螵:piāo,螺:luó,螽:zhōng,蟀:shuài,蟆:má,mò,蟊:máo,méng,蟋:xī,
蟏:xiāo,蟑:zhāng,蟒:mǎng,měng,蟛:péng,蟠:pán,fán,蟥:huáng,蟪:huì,蟫:yín,xún,
蟮:shàn,蟹:xiè,蟾:chán,蠃:luǒ,luó,guǒ,蠊:lián,蠋:zhú,蠓:měng,蠕:rú,
蠖:huò,yuè,蠡:lí,lǐ,luǒ,luó,lì,蠢:chǔn,蠲:juān,蠹:dù,蠼:qú,jué,血:xuè,xiě,衃:pēi,fǒu,
衄:nǜ,衅:xìn,行:xíng,háng,héng,xìng,hàng,衍:yǎn,yán,衎:kàn,kǎn,衒:xuàn,衔:xián,街:jiē,
衙:yá,yú,yù,衠:zhūn,衡:héng,衢:qú,衣:yī,yì,补:bǔ,表:biǎo,衩:chǎ,chà,
衫:shān,衬:chèn,衮:gǔn,衰:shuāi,suō,cuī,衲:nà,衷:zhōng,zhòng,衽:rèn,衾:qīn,
衿:jīn,qìn,袁:yuán,袂:mèi,yì,袄:ǎo,袅:niǎo,袆:huī,袈:jiā,袋:dài,
袍:páo,bào,袒:tǎn,zhàn,袖:xiù,袗:zhěn,袜:wà,mò,袢:pàn,fán,袤:mào,móu,袪:qū,
被:bèi,bì,pī,pì,袭:xí,袯:bó,袱:fú,袷:jiá,qiā,jiā,jié,袼:gē,luò,裁:cái,裂:liè,liě,
装:zhuāng,裆:dāng,裈:kūn,裉:kèn,裎:chéng,chěng,裒:póu,bāo,裔:yì,裕:yù,
裘:qiú,裙:qún,裛:yì,裟:shā,裢:lián,shāo,裣:liǎn,裤:kù,裥:jiǎn,
裨:bì,pí,裰:duō,裱:biǎo,裳:shang,cháng,裴:péi,féi,裸:luǒ,裹:guǒ,裼:tì,xī,
裾:jū,jù,褂:guà,褊:biǎn,pián,褐:hè,褒:bāo,褓:bǎo,褕:yú,tóu,褙:bèi,
褚:chǔ,zhǔ,zhě,褛:lǚ,褟:tā,褡:dā,褥:rù,nù,褪:tuì,tùn,褫:chǐ,褯:jiè,
褰:qiān,褴:lán,褶:zhě,dié,xí,襁:qiǎng,襄:xiāng,襕:lán,襚:suì,襜:chān,chàn,dān,
襞:bì,襟:jīn,襦:rú,襫:shì,襻:pàn,西:xī,要:yào,yāo,yǎo,覃:tán,qín,yǎn,
覆:fù,见:jiàn,xiàn,观:guān,guàn,觃:yàn,规:guī,觅:mì,视:shì,觇:chān,
览:lǎn,觉:jué,jiào,觊:jì,觋:xí,觌:dí,觎:yú,觏:gòu,觐:jìn,
觑:qù,qū,角:jiǎo,jué,lù,gǔ,觖:jué,kuì,guì,觚:gū,觜:zī,zuǐ,觞:shāng,觟:huà,xiè,解:jiě,jiè,xiè,
觥:gōng,触:chù,觫:sù,觭:jī,qǐ,qí,觯:zhì,觱:bì,觳:hú,què,jué,觿:xī,wéi,
言:yán,yàn,yín,訄:qiú,kāo,訇:hōng,jùn,hēng,訉:fàn,訚:yín,訾:zī,zǐ,詈:lì,詟:zhé,
詹:zhān,dàn,誉:yù,誊:téng,誓:shì,謇:jiǎn,謦:qǐng,qìng,警:jǐng,譬:pì,
讝:zhán,zhān,计:jì,订:dìng,讣:fù,认:rèn,讥:jī,讦:jié,讧:hòng,
讨:tǎo,让:ràng,讪:shàn,讫:qì,训:xùn,议:yì,讯:xùn,记:jì,
讱:rèn,讲:jiǎng,讳:huì,讴:ōu,讵:jù,讶:yà,讷:nè,许:xǔ,hǔ,
讹:é,论:lùn,lún,讻:xiōng,讼:sòng,讽:fěng,fèng,设:shè,访:fǎng,诀:jué,
证:zhèng,诂:gǔ,诃:hē,评:píng,诅:zǔ,识:shí,shì,zhì,诇:xiòng,诈:zhà,
诉:sù,诊:zhěn,诋:dǐ,诌:zhōu,词:cí,诎:qū,诏:zhào,诐:bì,
译:yì,诒:yí,诓:kuāng,诔:lěi,试:shì,诖:guà,诗:shī,诘:jí,jié,
诙:huī,诚:chéng,诛:zhū,诜:shēn,话:huà,诞:dàn,诟:gòu,诠:quán,
诡:guǐ,询:xún,诣:yì,诤:zhèng,该:gāi,详:xiáng,诧:chà,诨:hùn,
诩:xǔ,诫:jiè,诬:wū,语:yǔ,yù,诮:qiào,误:wù,诰:gào,诱:yòu,
诲:huì,诳:kuáng,说:shuō,shuì,yuè,诵:sòng,请:qǐng,诸:zhū,诹:zōu,诺:nuò,
读:dú,dòu,诼:zhuó,诽:fěi,课:kè,诿:wěi,谀:yú,谁:shuí,shéi,谂:shěn,
调:diào,tiáo,谄:chǎn,谅:liàng,谆:zhūn,谇:suì,谈:tán,谊:yì,谋:móu,
谌:chén,谍:dié,谎:huǎng,谏:jiàn,谐:xié,谑:xuè,谒:yè,谓:wèi,
谔:è,谕:yù,谖:xuān,谗:chán,谙:ān,谚:yàn,谛:dì,谜:mí,mèi,
谝:pián,piǎn,谞:xū,谟:mó,谠:dǎng,谡:sù,谢:xiè,谣:yáo,谤:bàng,
谥:shì,谦:qiān,谧:mì,谨:jǐn,谩:mán,màn,谪:zhé,谫:jiǎn,谬:miù,
谭:tán,谮:zèn,谯:qiáo,qiào,谰:lán,谱:pǔ,谲:jué,谳:yàn,谴:qiǎn,
谵:zhān,谶:chèn,谷:gǔ,lù,yù,谼:hóng,谿:xī,jī,豁:huō,huò,huá,豆:dòu,豇:jiāng,
豉:shì,chǐ,豌:wān,豕:shǐ,豚:tún,dūn,dùn,象:xiàng,豢:huàn,豨:xī,豪:háo,
豫:yù,xiè,shū,豮:fén,豳:bīn,bān,豸:zhì,zhài,豹:bào,豺:chái,貂:diāo,貅:xiū,
貆:huán,huān,貉:háo,hé,mò,mà,貊:mò,má,貌:mào,mò,貔:pí,貘:mò,贝:bèi,贞:zhēn,
负:fù,贡:gòng,财:cái,责:zé,贤:xián,败:bài,账:zhàng,货:huò,
质:zhì,贩:fàn,贪:tān,贫:pín,贬:biǎn,购:gòu,贮:zhù,贯:guàn,
贰:èr,贱:jiàn,贲:bēn,bì,贳:shì,贴:tiē,贵:guì,贶:kuàng,贷:dài,
贸:mào,费:fèi,贺:hè,贻:yí,贼:zéi,贽:zhì,贾:jiǎ,gǔ,贿:huì,
赀:zī,赁:lìn,赂:lù,赃:zāng,资:zī,赅:gāi,赆:jìn,赇:qiú,
赈:zhèn,赉:lài,赊:shē,赋:fù,赌:dǔ,赍:jī,赎:shú,赏:shǎng,
赐:cì,赑:bì,赒:zhōu,赓:gēng,赔:péi,赕:dǎn,赖:lài,赗:fèng,
赘:zhuì,赙:fù,赚:zhuàn,zuàn,赛:sài,赜:zé,赝:yàn,赞:zàn,赟:yūn,
赠:zèng,赡:shàn,赢:yíng,赣:gàn,赤:chì,赦:shè,cè,赧:nǎn,赪:chēng,
赫:hè,shì,赭:zhě,走:zǒu,赳:jiū,jiù,赴:fù,赵:zhào,赶:gǎn,qián,起:qǐ,
趁:chèn,zhēn,chén,niǎn,zhěn,趄:jū,qiè,超:chāo,chǎo,chào,tiào,越:yuè,huó,趋:qū,趑:zī,cì,趔:liè,趟:tàng,zhēng,zhèng,chéng,tāng,
趣:qù,cù,qū,cǒu,zōu,趯:tì,yuè,yào,趱:zǎn,足:zú,jù,趴:pā,趵:bào,bō,zhuó,chuò,páo,趸:dǔn,趺:fū,
趼:jiǎn,yàn,yán,jiān,趾:zhǐ,趿:tā,sà,qì,跂:qí,qǐ,qì,jī,zhī,跃:yuè,跄:qiāng,qiàng,跆:tái,跋:bá,bèi,
跌:diē,dié,tú,跎:tuó,跏:jiā,跐:cī,cǐ,zǐ,跑:pǎo,páo,bó,跖:zhí,跗:fū,fù,跚:shān,
跛:bǒ,bì,pō,距:jù,跞:lì,luò,跟:gēn,跣:xiǎn,xiān,sǔn,跤:jiāo,qiāo,跨:kuà,kù,kuā,kuǎ,跪:guì,
跬:kuǐ,xiè,路:lù,luò,跱:zhì,跳:tiào,diào,táo,践:jiàn,跶:dá,da,跷:qiāo,跸:bì,
跹:xiān,跺:duò,跻:jī,跽:jì,踅:xué,chì,踉:liáng,liàng,láng,làng,踊:yǒng,踌:chóu,
踏:tà,tā,踒:wō,wēi,ruí,踔:chuō,diào,zhuō,tiào,chuò,踝:huái,踞:jù,踟:chí,踢:tī,dié,踣:bó,pòu,
踦:yǐ,qī,jī,jǐ,yì,踩:cǎi,kuí,踪:zōng,踬:zhì,踮:diǎn,踯:zhí,踱:duó,chuò,踵:zhǒng,zhòng,
踶:dì,zhì,tí,chí,shì,踹:chuài,shuàn,duàn,chuǎn,踺:jiàn,踽:jǔ,蹀:dié,蹁:pián,蹂:róu,rǒu,蹄:tí,dì,
蹅:chǎ,zhā,蹇:jiǎn,蹈:dǎo,蹉:cuō,蹊:qī,xī,蹋:tà,蹐:jí,蹑:niè,
蹒:pán,蹙:cù,蹚:tāng,tàng,chēng,蹜:sù,蹢:dí,zhí,蹦:bèng,蹩:bié,蹬:dēng,dèng,
蹭:cèng,céng,蹯:fán,蹰:chú,蹲:dūn,zún,cún,zūn,cǔn,cuán,qǔn,蹴:cù,zú,jiu,蹶:jué,juě,guì,蹼:pǔ,蹽:liāo,
蹾:dūn,蹿:cuān,躁:zào,躅:zhú,zhuó,躇:chú,chuò,躏:lìn,躐:liè,躔:chán,zhàn,
躜:zuān,躞:xiè,身:shēn,juān,躬:gōng,躯:qū,躲:duǒ,躺:tǎng,tàng,车:chē,jū,
轧:yà,zhá,gá,轨:guǐ,轩:xuān,轪:dài,轫:rèn,转:zhuǎn,zhuàn,zhuǎi,轭:è,轮:lún,
软:ruǎn,轰:hōng,轱:gū,轲:kē,kě,轳:lú,轴:zhóu,zhòu,轵:zhǐ,轶:yì,
轷:hū,轸:zhěn,轹:lì,轺:yáo,轻:qīng,轼:shì,载:zài,zǎi,轾:zhì,
轿:jiào,辀:zhōu,辁:quán,辂:lù,较:jiào,辄:zhé,辅:fǔ,辆:liàng,
辇:niǎn,辈:bèi,辉:huī,辊:gǔn,辋:wǎng,辌:liáng,辍:chuò,辎:zī,
辏:còu,辐:fú,辑:jí,辒:wēn,输:shū,辔:pèi,辕:yuán,辖:xiá,
辗:niǎn,zhǎn,辘:lù,辙:zhé,辚:lín,辛:xīn,辜:gū,辞:cí,辟:pì,bì,mǐ,pī,
辣:là,辨:biàn,biǎn,bàn,piàn,辩:biàn,辫:biàn,辰:chén,辱:rǔ,边:biān,bian,辽:liáo,
达:dá,tì,tà,辿:chān,chán,迁:qiān,迂:yū,迄:qì,迅:xùn,过:guò,guō,迈:mài,
迎:yíng,yìng,运:yùn,yǔn,近:jìn,迓:yà,返:fǎn,迕:wù,wǔ,还:hái,huán,fú,这:zhè,zhèi,
进:jìn,远:yuǎn,违:wéi,连:lián,迟:chí,迢:tiáo,迤:yí,yǐ,tuó,迥:jiǒng,
迦:jiā,xiè,迨:dài,迩:ěr,迪:dí,迫:pò,pǎi,迭:dié,yì,dá,迮:zé,zuò,述:shù,
迳:jìng,迷:mí,mì,迸:bèng,迹:jì,jī,迺:nǎi,追:zhuī,duī,tuī,退:tuì,送:sòng,
适:shì,kuò,逃:táo,逄:páng,féng,逅:hòu,逆:nì,选:xuǎn,逊:xùn,逋:bū,
逍:xiāo,透:tòu,shū,逐:zhú,dí,zhòu,tún,逑:qiú,递:dì,途:tú,逖:tì,逗:dòu,zhù,tóu,qí,
通:tōng,tòng,逛:guàng,kuáng,逝:shì,逞:chěng,yíng,速:sù,造:zào,cào,cāo,逡:qūn,xùn,suō,逢:féng,péng,páng,
逦:lǐ,逭:huàn,逮:dǎi,dài,dì,逯:lù,dài,逴:chuō,chuò,逵:kuí,kuǐ,逶:wēi,逸:yì,
逻:luó,逼:bī,逾:yú,dòu,遁:dùn,qūn,xún,遂:suì,suí,遄:chuán,遆:tí,dì,遇:yù,yóng,ǒu,
遍:biàn,遏:è,遐:xiá,遑:huáng,遒:qiú,qiū,道:dào,dǎo,遗:yí,wèi,遘:gòu,
遛:liú,liù,遢:tà,ta,tā,遣:qiǎn,qiàn,遥:yáo,遨:áo,遭:zāo,遮:zhē,遴:lín,lìn,
遵:zūn,遹:yù,遽:jù,qú,避:bì,邀:yāo,邂:xiè,邃:suì,邈:miǎo,miáo,
邋:lā,liè,邑:yì,è,邓:dèng,shān,邕:yōng,yǒng,邗:hán,邘:yú,邙:máng,邛:qióng,
邝:kuàng,邠:bīn,邡:fāng,fàng,邢:xíng,gěng,那:nà,nā,nuó,nuò,nèi,nǎ,něi,né,nǎi,nè,邦:bāng,邨:cūn,邪:xié,yá,yé,xú,shé,
邬:wū,邮:yóu,邯:hán,hàn,邰:tái,邱:qiū,邲:bì,biàn,邳:pī,邴:bǐng,
邵:shào,邶:bèi,邸:dǐ,邹:zōu,邺:yè,qiū,邻:lín,邽:guī,邾:zhū,
邿:shī,郁:yù,郃:hé,xiá,郄:qiè,xì,郅:zhì,jí,郇:huán,xún,郈:hòu,郊:jiāo,
郎:láng,làng,郏:jiá,郐:kuài,郑:zhèng,郓:yùn,郗:xī,chī,郘:lǚ,郚:wú,yú,
郛:fú,郜:gào,郝:hǎo,shì,郡:jùn,郢:yǐng,chéng,郤:xì,郦:lì,郧:yún,
部:bù,pǒu,郪:qī,郫:pí,郭:guō,guó,郯:tán,郴:chēn,lán,郸:dān,都:dōu,dū,
郾:yǎn,yān,郿:méi,鄀:ruò,鄂:è,鄃:shū,鄄:juàn,鄅:yǔ,鄌:táng,
鄑:zī,鄗:hào,qiāo,jiāo,鄘:yōng,鄙:bǐ,鄚:mào,mò,鄜:fū,lù,鄞:yín,鄠:hù,
鄢:yān,鄣:zhāng,zhàng,鄫:zēng,céng,鄯:shàn,鄱:pó,pí,pán,鄹:zōu,jù,酂:cuó,zàn,酃:líng,
酅:xī,酆:fēng,酉:yǒu,酊:dīng,dǐng,酋:qiú,酌:zhuó,配:pèi,酎:zhòu,
酏:yǐ,yí,酐:gān,hàng,酒:jiǔ,酗:xù,酚:fēn,酝:yùn,酞:tài,酡:tuó,duò,
酢:cù,zuò,酣:hān,hàn,酤:gū,酥:sū,酦:pò,pō,fā,酩:mǐng,酪:lào,luò,lù,酬:chóu,
酮:tóng,dòng,chóng,酯:zhǐ,酰:xiān,酱:jiàng,酲:chéng,酴:tú,酵:jiào,酶:méi,
酷:kù,酸:suān,酹:lèi,酺:pú,酽:yàn,酾:shāi,shī,酿:niàng,niáng,醅:pēi,
醇:chún,醉:zuì,醋:cù,zuò,醌:kūn,醍:tí,tǐ,醐:hú,醑:xǔ,醒:xǐng,chéng,jīng,
醚:mí,醛:quán,chuò,醢:hǎi,醨:lí,醪:láo,醭:bú,醮:jiào,qiáo,zhàn,醯:xī,
醴:lǐ,醵:jù,醺:xūn,醾:mí,采:cǎi,cài,釉:yòu,释:shì,里:lǐ,li,
重:zhòng,chóng,tóng,野:yě,shù,量:liàng,liáng,釐:lí,xī,lái,tāi,lài,xǐ,金:jīn,jìn,釜:fǔ,鉴:jiàn,銎:qióng,qiōng,
銮:luán,鋆:yún,jūn,鋈:wù,錾:zàn,鍪:móu,鎏:liú,鏊:ào,鏖:áo,biāo,
鐾:bèi,鑫:xīn,xùn,钆:gá,钇:yǐ,针:zhēn,钉:dīng,dìng,钊:zhāo,钋:pō,
钌:liǎo,liào,钍:tǔ,钎:qiān,钏:chuàn,钐:shān,shàn,钒:fán,钓:diào,钔:mén,
钕:nǚ,钖:yáng,钗:chāi,钘:xíng,钙:gài,钚:bù,钛:tài,钜:jù,
钝:dùn,钞:chāo,钟:zhōng,钠:nà,钡:bèi,钢:gāng,gàng,钣:bǎn,钤:qián,
钥:yào,yuè,钦:qīn,钧:jūn,钨:wū,钩:gōu,钪:kàng,钫:fāng,钬:huǒ,
钭:tǒu,dǒu,钮:niǔ,钯:bǎ,pá,钰:yù,钱:qián,钲:zhēng,钳:qián,钴:gǔ,
钵:bō,钷:pǒ,钹:bó,钺:yuè,钻:zuān,zuàn,钼:mù,钽:tǎn,钾:jiǎ,
钿:diàn,tián,铀:yóu,铁:tiě,铂:bó,铃:líng,铄:shuò,铅:qiān,yán,铆:mǎo,
铈:shì,铉:xuàn,铊:tā,tuó,铋:bì,铌:ní,铍:pī,pí,铎:duó,铏:xíng,
铐:kào,铑:lǎo,铒:ěr,铕:yǒu,铖:chéng,铗:jiá,铘:yé,铙:náo,
铚:zhì,铛:dāng,chēng,铜:tóng,铝:lǚ,铞:diào,铟:yīn,铠:kǎi,铡:zhá,
铢:zhū,铣:xǐ,xiǎn,铤:dìng,tǐng,铥:diū,铧:huá,铨:quán,铩:shā,铪:hā,
铫:diào,yáo,铬:gè,铭:míng,铮:zhēng,zhèng,铯:sè,铰:jiǎo,铱:yī,铲:chǎn,
铳:chòng,铴:tāng,tàng,铵:ǎn,银:yín,铷:rú,铸:zhù,铹:láo,铺:pù,pū,
铻:wú,yǔ,铼:lái,铽:tè,链:liàn,铿:kēng,销:xiāo,锁:suǒ,锂:lǐ,
锃:zèng,锄:chú,锅:guō,锆:gào,锇:é,锈:xiù,锉:cuò,锊:lüè,
锋:fēng,锌:xīn,锍:liǔ,锎:kāi,锏:jiǎn,jiàn,锐:ruì,锑:tī,锒:láng,
锓:qǐn,锔:jū,jú,锕:ā,锖:qiāng,锗:zhě,锘:nuò,错:cuò,锚:máo,
锛:bēn,锜:qí,锝:dé,锞:kè,锟:kūn,锡:xī,锢:gù,锣:luó,
锤:chuí,锥:zhuī,锦:jǐn,锧:zhì,锨:xiān,锩:juǎn,锪:huō,huò,锫:péi,
锬:tán,xiān,锭:dìng,键:jiàn,锯:jù,jū,锰:měng,锱:zī,锲:qiè,锳:yīng,
锴:kǎi,锵:qiāng,锶:sī,锷:è,锸:chā,锹:qiāo,锺:zhōng,锻:duàn,
锼:sōu,锽:huáng,锾:huán,锿:āi,镀:dù,镁:měi,镂:lòu,镃:zī,
镄:fèi,镅:méi,镆:mò,镇:zhèn,镈:bó,镉:gé,镊:niè,镋:tǎng,
镌:juān,镍:niè,镎:ná,镏:liú,liù,镐:gǎo,hào,镑:bàng,镒:yì,镓:jiā,
镔:bīn,镕:róng,镖:biāo,镗:tāng,táng,镘:màn,镚:bèng,镛:yōng,镜:jìng,
镝:dī,dí,镞:zú,镠:liú,镡:chán,xín,tán,镢:jué,镣:liào,镤:pú,镥:lǔ,
镦:duì,duī,dūn,镧:lán,镨:pǔ,镩:cuān,镪:qiāng,qiǎng,镫:dèng,镬:huò,镭:léi,
镮:huán,镯:zhuó,镰:lián,镱:yì,镲:chǎ,镳:biāo,镴:là,镵:chán,
镶:xiāng,长:zhǎng,cháng,门:mén,闩:shuān,闪:shǎn,闫:yán,闭:bì,问:wèn,
闯:chuǎng,闰:rùn,闱:wéi,闲:xián,闳:hóng,间:jiān,jiàn,闵:mǐn,闶:kāng,kàng,
闷:mèn,mēn,闸:zhá,闹:nào,闺:guī,闻:wén,闼:tà,闽:mǐn,闾:lǘ,
闿:kǎi,阀:fá,阁:gé,阂:hé,阃:kǔn,阄:jiū,阅:yuè,阆:láng,làng,
阇:dū,shé,阈:yù,阉:yān,阊:chāng,阋:xì,阌:wén,阍:hūn,阎:yán,
阏:è,yān,阐:chǎn,阑:lán,阒:qù,阔:kuò,阕:què,阖:hé,阗:tián,
阘:dá,tà,阙:quē,què,阚:hǎn,kàn,阜:fù,队:duì,阡:qiān,阪:bǎn,阮:ruǎn,yuán,
阱:jǐng,防:fáng,阳:yáng,阴:yīn,阵:zhèn,阶:jiē,阻:zǔ,zhù,阼:zuò,
阽:diàn,yán,阿:ā,ē,ě,ǎ,à,a,陀:tuó,duò,陂:bēi,pí,bì,pō,附:fù,bù,fū,际:jì,陆:lù,liù,陇:lǒng,
陈:chén,陉:xíng,陋:lòu,陌:mò,降:jiàng,xiáng,xiàng,陎:shū,限:xiàn,wěn,陑:ér,
陔:gāi,陕:shǎn,陛:bì,陞:shēng,陟:zhì,dé,陡:dǒu,院:yuàn,除:chú,zhù,shū,
陧:niè,陨:yǔn,险:xiǎn,陪:péi,陬:zōu,zhé,陲:chuí,陴:pí,pī,bì,陵:líng,
陶:táo,yáo,dào,陷:xiàn,隃:shù,yú,yáo,隅:yú,隆:lóng,lōng,隈:wēi,隋:suí,duò,tuǒ,tuō,隍:huáng,
随:suí,隐:yǐn,隔:gé,rǒng,jī,隗:kuí,wěi,guī,隘:ài,è,隙:xì,障:zhàng,zhāng,隧:suì,zhuì,
隩:ào,yù,隰:xí,xiè,隳:huī,隶:lì,dài,yì,dì,隹:zhuī,cuī,wéi,隺:hú,hè,què,隼:sǔn,隽:juàn,jùn,
难:nán,nàn,雀:què,qiāo,qiǎo,雁:yàn,雄:xióng,雅:yǎ,yā,yá,集:jí,雇:gù,hù,雉:zhì,kǎi,yǐ,sì,
雊:gòu,雌:cí,雍:yōng,雎:jū,雏:chú,雒:luò,雕:diāo,雠:chóu,
雨:yǔ,yù,雩:yú,yù,xū,雪:xuě,雯:wén,雱:pāng,páng,fāng,雳:lì,零:líng,lián,雷:léi,lèi,
雹:báo,雾:wù,需:xū,nuò,rú,ruǎn,霁:jì,霄:xiāo,xiào,霅:zhà,zhá,shà,sà,yì,霆:tíng,震:zhèn,shēn,
霈:pèi,霉:méi,霍:huò,hè,suǒ,霎:shà,霏:fēi,霓:ní,霖:lín,霜:shuāng,
霞:xiá,霨:wèi,霪:yín,霭:ǎi,霰:xiàn,sǎn,露:lù,lòu,霸:bà,pò,霹:pī,
霾:mái,lí,青:qīng,jīng,靓:jìng,liàng,靖:jìng,静:jìng,靛:diàn,非:fēi,fěi,靠:kào,
靡:mí,mǐ,má,面:miàn,靥:yè,革:gé,jí,靬:qián,jiān,kān,hàn,靰:wù,靳:jìn,靴:xuē,
靶:bǎ,bà,靸:sǎ,tā,靺:mò,wà,靼:dá,靽:bàn,靿:yào,鞁:bèi,bài,bì,鞅:yāng,yàng,yǎng,
鞋:xié,wā,鞍:ān,鞑:dá,鞒:qiáo,鞔:mán,mèn,鞘:qiào,shāo,鞠:jū,qū,qiōng,鞡:la,
鞣:róu,鞧:qiū,鞨:hé,shé,mò,鞫:jū,qū,鞬:jiān,jiàn,鞭:biān,鞮:dī,鞯:jiān,
鞲:gōu,鞳:tà,鞴:bèi,fú,bù,bài,韂:chàn,韦:wéi,韧:rèn,韨:fú,韩:hán,
韪:wěi,韫:yùn,韬:tāo,韭:jiǔ,音:yīn,韵:yùn,韶:sháo,页:yè,
顶:dǐng,顷:qǐng,顸:hān,项:xiàng,顺:shùn,须:xū,顼:xū,顽:wán,
顾:gù,顿:dùn,dú,颀:qí,颁:bān,颂:sòng,颃:háng,预:yù,颅:lú,
领:lǐng,颇:pǒ,pō,颈:jǐng,gěng,颉:jié,xié,颊:jiá,颋:tǐng,颌:hé,gé,颍:yǐng,
颎:jiǒng,颏:kē,ké,颐:yí,频:pín,颓:tuí,颔:hàn,颖:yǐng,颗:kē,
题:tí,颙:yóng,颚:è,颛:zhuān,颜:yán,额:é,颞:niè,颟:mān,
颠:diān,颡:sǎng,颢:hào,颤:chàn,zhàn,颥:rú,颦:pín,颧:quán,风:fēng,
飏:yáng,飐:zhǎn,飑:biāo,飒:sà,飓:jù,飔:sī,飕:sōu,飗:liú,
飘:piāo,飙:biāo,飞:fēi,食:shí,sì,yì,飧:sūn,飨:xiǎng,餍:yàn,餐:cān,sūn,
餮:tiè,饔:yōng,饕:tāo,饥:jī,饧:táng,xíng,饨:tún,饩:xì,饪:rèn,
饫:yù,饬:chì,饭:fàn,饮:yǐn,yìn,饯:jiàn,饰:shì,饱:bǎo,饲:sì,
饳:duò,饴:yí,饵:ěr,饶:ráo,饷:xiǎng,饸:hé,饹:le,gē,饺:jiǎo,
饻:xī,饼:bǐng,饽:bō,饿:è,馁:něi,馃:guǒ,馄:hún,馅:xiàn,
馆:guǎn,馇:chā,zha,馈:kuì,馉:gǔ,馊:sōu,馋:chán,馌:yè,馍:mó,
馏:liú,liù,馐:xiū,馑:jǐn,馒:mán,馓:sǎn,馔:zhuàn,馕:náng,nǎng,首:shǒu,
馗:kuí,qiú,馘:guó,xù,香:xiāng,馝:bì,馞:bó,pò,馥:fù,bì,馧:yūn,wò,馨:xīn,
马:mǎ,驭:yù,驮:tuó,duò,驯:xùn,xún,驰:chí,驱:qū,驲:rì,驳:bó,
驴:lǘ,驵:zǎng,驶:shǐ,驷:sì,驸:fù,驹:jū,驺:zōu,驻:zhù,
驼:tuó,驽:nú,驾:jià,驿:yì,骀:dài,tái,骁:xiāo,骂:mà,骃:yīn,
骄:jiāo,骅:huá,骆:luò,骇:hài,骈:pián,骉:biāo,骊:lí,骋:chěng,
验:yàn,骍:xīng,骎:qīn,骏:jùn,骐:qí,骑:qí,骒:kè,骓:zhuī,
骕:sù,骖:cān,骗:piàn,骘:zhì,骙:kuí,骚:sāo,骛:wù,骜:ào,áo,
骝:liú,骞:qiān,骟:shàn,骠:biāo,piào,骡:luó,骢:cōng,骣:chǎn,骤:zhòu,
骥:jì,骦:shuāng,骧:xiāng,骨:gǔ,gū,gú,骰:tóu,gǔ,骱:jiè,jiá,xiè,骶:dǐ,骷:kū,
骸:hái,gāi,骺:hóu,骼:gé,髀:bì,髁:kē,kuà,髂:qià,gé,髃:yú,髅:lóu,
髋:kuān,髌:bìn,髎:liáo,髑:dú,髓:suǐ,高:gāo,gào,髡:kūn,髢:dí,
髦:máo,髫:tiáo,髭:zī,髯:rán,髹:xiū,髻:jì,jié,髽:zhuā,鬃:zōng,
鬈:quán,鬏:jiū,鬒:zhěn,鬓:bìn,鬘:mán,鬟:huán,鬣:liè,鬯:chàng,
鬲:gé,lì,è,鬶:guī,鬷:zōng,zěng,鬻:yù,zhōu,jū,鬼:guǐ,魁:kuí,kuǐ,kuài,魂:hún,魃:bá,
魄:pò,bó,tuò,魅:mèi,魆:xū,魇:yǎn,魈:xiāo,魉:liǎng,魋:tuí,chuí,魍:wǎng,
魏:wèi,wéi,wēi,魑:chī,魔:mó,鱼:yú,鱽:dāo,鱾:jǐ,鱿:yóu,鲀:tún,
鲁:lǔ,鲂:fáng,鲃:bā,鲅:bà,bō,鲆:píng,鲇:nián,鲈:lú,鲉:yóu,
鲊:zhǎ,鲋:fù,鲌:bà,bó,鲍:bào,鲎:hòu,鲏:pí,鲐:tái,鲑:guī,xié,
鲒:jié,鲔:wěi,鲕:ér,鲖:tóng,鲗:zéi,鲘:hòu,鲙:kuài,鲚:jì,
鲛:jiāo,鲜:xiān,xiǎn,鲝:zhǎ,鲞:xiǎng,鲟:xún,鲠:gěng,鲡:lí,鲢:lián,
鲣:jiān,鲤:lǐ,鲥:shí,鲦:tiáo,鲧:gǔn,鲨:shā,鲩:huàn,鲪:jūn,
鲫:jì,鲬:yǒng,鲭:qīng,zhēng,鲮:líng,鲯:qí,鲰:zōu,鲱:fēi,鲲:kūn,
鲳:chāng,鲴:gù,鲵:ní,鲷:diāo,鲸:jīng,鲹:shēn,鲺:shī,鲻:zī,
鲼:fèn,鲽:dié,鲾:bī,鲿:cháng,鳀:tí,鳁:wēn,鳂:wēi,鳃:sāi,
鳄:è,鳅:qiū,鳇:huáng,鳈:quán,鳉:jiāng,鳊:biān,鳌:áo,鳍:qí,
鳎:tǎ,鳏:guān,鳐:yáo,鳑:páng,鳒:jiān,鳓:lè,鳔:biào,鳕:xuě,
鳖:biē,鳗:mán,鳘:mǐn,鳙:yōng,鳚:wèi,鳛:xí,鳜:guì,鳝:shàn,
鳞:lín,鳟:zūn,鳠:hù,鳡:gǎn,鳢:lǐ,鳣:zhān,鳤:guǎn,鴤:zhōng,
鸟:niǎo,diǎo,鸠:jiū,鸡:jī,鸢:yuān,鸣:míng,鸤:shī,鸥:ōu,鸦:yā,
鸧:cāng,鸨:bǎo,鸩:zhèn,鸪:gū,鸫:dōng,鸬:lú,鸭:yā,鸮:xiāo,
鸯:yāng,鸰:líng,鸱:chī,鸲:qú,鸳:yuān,鸵:tuó,鸶:sī,鸷:zhì,
鸸:ér,鸹:guā,鸺:xiū,鸻:héng,鸼:zhōu,鸽:gē,鸾:luán,鸿:hóng,
鹀:wú,鹁:bó,鹂:lí,鹃:juān,鹄:gǔ,hú,鹅:é,鹆:yù,鹇:xián,
鹈:tí,鹉:wǔ,鹊:què,鹋:miáo,鹌:ān,鹍:kūn,鹎:bēi,鹏:péng,
鹐:qiān,鹑:chún,鹒:gēng,鹔:sù,鹕:hú,鹖:hé,鹗:è,鹘:gǔ,hú,
鹙:qiū,鹚:cí,鹛:méi,鹜:wù,鹝:yì,鹞:yào,鹟:wēng,鹠:liú,
鹡:jí,jī,鹢:yì,鹣:jiān,鹤:hè,鹦:yīng,鹧:zhè,鹨:liù,鹩:liáo,
鹪:jiāo,鹫:jiù,鹬:yù,鹭:lù,鹮:huán,鹯:zhān,鹰:yīng,鹱:hù,
鹲:méng,鹳:guàn,鹴:shuāng,鹾:cuó,鹿:lù,lǘ,麀:yōu,麂:jǐ,麇:jūn,qún,
麈:zhǔ,麋:mí,麑:ní,麒:qí,麓:lù,麖:jīng,麝:shè,麟:lín,
麦:mài,麸:fū,麹:qū,麻:má,mā,麽:mó,má,ma,me,麾:huī,黄:huáng,黇:tiān,
黉:hóng,黍:shǔ,黎:lí,黏:nián,黑:hēi,黔:qián,默:mò,黛:dài,
黜:chù,黝:yǒu,yī,黟:yī,黠:xiá,黡:yǎn,黢:qū,黥:qíng,黧:lí,lái,
黩:dú,黪:cǎn,黯:àn,ān,黹:zhǐ,xiàn,黻:fú,黼:fǔ,黾:mǐn,miǎn,měng,鼋:yuán,
鼍:tuó,鼎:dǐng,zhēn,鼐:nài,鼒:zī,鼓:gǔ,鼗:táo,鼙:pí,鼠:shǔ,
鼢:fén,鼩:qú,鼫:shí,鼬:yòu,鼯:wú,鼱:jīng,鼷:xī,鼹:yǎn,
鼻:bí,鼽:qiú,鼾:hān,齁:hōu,kù,齇:zhā,齉:nàng,齐:qí,jì,齑:jī,
齿:chǐ,龀:chèn,龁:hé,龂:yín,龃:jǔ,龄:líng,龅:bāo,龆:tiáo,
龇:zī,龈:kěn,yín,龉:yǔ,龊:chuò,龋:qǔ,龌:wò,龙:lóng,龚:gōng,
龛:kān,龟:guī,jūn,qiū,龠:yuè,龢:hé,鿍:gàng,鿎:tǎ,dá,鿏:mài
`.trim();

